/**
 * Ultron Turing HTTP API Service
 * Complete API service using Ultron Turing intelligent middleware
 */

import { PowerMeter, SystemAlert, PowerData as RealTimeData, ApiResponse } from '../types';
import { ClimateDevice } from '../types/climate';
import { logger } from '../utils/logger';
import { getBaseSN } from './deviceSubscriptionManager';
import { getFromCache, setToCache } from '../utils/apiCache';
import { reportCacheService } from './reportCacheService';

// Ultron Turing Device interface (updated for new API response)
interface Device {
  // Identification
  sn: string;                      // Original SN for API calls
  deviceSN?: string;               // Full SN with endpoint (e.g., "UT2Z0S-xxx#1")
  baseSN?: string;                 // Base SN without endpoint
  endpoint?: string;               // Endpoint number (e.g., "1")

  // Display names
  name?: string;
  displayName?: string;            // API display name
  nickname?: string | null;        // User custom nickname

  // Metadata
  online: boolean;
  location?: string;
  traits?: string[];
  type?: string;

  // Organization
  groupId?: string;
  groupName?: string;

  // API metadata
  _source?: string;
  detectedCapabilities?: string[] | null;
  attributes?: Record<string, any>;
  
  // Sensor profile (normalized from attributes.sensorProfile for easier access)
  sensorProfile?: Record<string, any>;
}

/**
 * Get display name for a device (priority: nickname > displayName > name > sn)
 */
export function getDeviceDisplayName(device: Partial<Device>): string {
  return device.nickname || device.displayName || device.name || device.sn || 'Unknown Device';
}

// API Response Types
interface LocationResponse {
  success?: boolean;
  data?: {
    locations?: Location[];
    groups?: Location[];
    [key: string]: any;
  };
  locations?: Location[];
  groups?: Location[];
  error?: any;
  [key: string]: any;
}

// Response for executeCommand (does not follow standard ApiResponse format)
interface CommandResponse {
  success?: boolean;
  result?: Array<{
    success: boolean;
    result?: {
      valid?: boolean;
      error?: number;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  }>;
  error?: string;
  hint?: string;
  availableCommands?: string[];
  example?: unknown;
  message?: string;
  timestamp?: string;
}

// Response for generateReport
interface ReportResponse {
  success?: boolean;
  data?: {
    devices?: Record<string, {
      currentPower?: number;
      usage?: unknown[];
      total?: number;
      dataPoints?: unknown[];
      [key: string]: unknown;
    }>;
    chartData?: Array<{
      time?: string;
      [key: string]: unknown;
    }>;
    type?: string;
    [key: string]: unknown;
  };
  peakPower?: number;
  message?: string;
  timestamp?: string;
  [key: string]: unknown;
}

interface Location {
  id: string;
  groupId?: string;
  name: string;
  groupName?: string;
  devices?: string[];
}

interface CommandParams {
  brightness?: number;
  temperature?: number;  // For setColorTemperature (2000-9000K) or setTemperature (19-30°C for HVAC)
  colorTemp?: number;   // Legacy: for ColorAbsolute
  color?: { r: number; g: number; b: number };
  mode?: string;        // For setMode (cool, heat, dry, fan-only)
  speed?: string;       // Legacy
  openPercent?: number;
  // Device control commands
  on?: boolean;
  // HVAC commands (legacy - new API uses simplified commands)
  thermostatTemperatureSetpoint?: number;
  thermostatMode?: string;
  fanSpeed?: string;    // For setFanSpeed (auto, low, medium, high)
  // Allow any other command parameters
  [key: string]: unknown;
}

interface AutomationTrigger {
  type: 'device' | 'time' | 'sensor';
  config: Record<string, unknown>;
}

interface AutomationAction {
  type: string;
  config: Record<string, unknown>;
}

interface ReportParams {
  type: string;
  devices: string[];  // 必填 - MCP 更新後 devices 為必填參數
  period?: string;
  startTime?: string;
  endTime?: string;
  granularity?: 'minute' | 'hour' | 'day' | 'auto';
  format?: string;
  energyDataFormat?: string;
  timezone?: string;
  // Allow type conversion to Record<string, unknown>
  [key: string]: unknown;
}

/**
 * Notification Preferences - User notification channel preferences
 * Supports alert, automation, and battery notification types
 * Each type can have websocket and/or email channels
 */
export interface NotificationPreferences {
  alert?: ('websocket' | 'email')[];
  automation?: ('websocket' | 'email')[];
  battery?: ('websocket' | 'email')[];
  notificationEmail?: string;  // Custom email address for notifications (uses user profile email if not set)
}

// Always use remote Turing API
// Get Turing API URL based on environment
function getTuringApiUrl(): string {
  // Check if OAuth is being used and get environment from session
  const authMode = sessionStorage.getItem('auth_mode');
  if (authMode === 'oauth') {
    try {
      // Import mcpOAuthService dynamically to avoid circular dependency
      const { mcpOAuthService } = require('./mcpOAuthService');
      const session = mcpOAuthService.getSession();
      
      // 🆕 Priority 1: Use apiUrl from session if provided by backend
      if (session?.apiUrl) {
        logger.log('🔧 Using backend-provided API URL:', session.apiUrl);
        return session.apiUrl;
      }
      
      // Priority 2: Use environment to determine URL
      if (session?.environment === 'staging') {
        const stagingUrl = process.env.REACT_APP_TURING_STAGING_URL || 'https://turing-staging.ultronsmart.com';
        logger.log('🔧 Using staging API URL:', stagingUrl);
        return stagingUrl;
      }
    } catch (e) {
      logger.warn('⚠️ Failed to get OAuth session for environment check:', e);
    }
  }
  
  // Default to production
  const productionUrl = 'https://turing.ultronsmart.com';
  logger.log('🔧 Using production API URL:', productionUrl);
  return productionUrl;
}

const TURING_API_URL = getTuringApiUrl();
logger.log('🔧 TURING_API_URL:', TURING_API_URL);

/**
 * Get OAuth access token from sessionStorage (with localStorage migration fallback)
 * ✅ Uses sessionStorage for better security
 */
function getOAuthAccessToken(): string | null {
  // Try sessionStorage first (new location)
  let accessToken = sessionStorage.getItem('access_token');
  if (!accessToken) {
    const tokens = sessionStorage.getItem('ultron_tokens');
    if (tokens) {
      try {
        const session = JSON.parse(tokens);
        accessToken = session.accessToken;
      } catch (e) {
        logger.error('Error parsing ultron_tokens from sessionStorage:', e);
      }
    }
  }
  
  // Migrate from localStorage if exists (backward compatibility)
  if (!accessToken) {
    const oldTokens = localStorage.getItem('ultron_tokens');
    if (oldTokens) {
      try {
        const session = JSON.parse(oldTokens);
        sessionStorage.setItem('ultron_tokens', oldTokens);
        localStorage.removeItem('ultron_tokens');
        accessToken = session.accessToken;
        logger.log('✅ Migrated tokens from localStorage to sessionStorage');
      } catch (e) {
        logger.error('Error migrating tokens:', e);
      }
    }
  }
  
  return accessToken;
}

// Get credentials from localStorage (saved by user in Settings)
function getCredentials() {
  const stored = localStorage.getItem('ultron-energy-credentials');
  logger.log('📦 Raw stored credentials:', stored ? 'Found' : 'Not found');
  if (stored) {
    try {
      const creds = JSON.parse(stored);
      // ✅ 使用 secure 方法，自动脱敏，并移除 apiKeyLength（不泄露密钥长度）
      logger.secure('Parsed credentials:', {
        hasApiKey: !!creds.apiKey,
        // 移除 apiKeyLength - 不泄露密钥长度信息
        appId: creds.appId,
        groupId: creds.groupId
      });
      // All credentials from user settings - no defaults
      return {
        apiKey: creds.apiKey || '',
        appId: creds.appId || '',
        groupId: creds.groupId || ''
      };
    } catch (e) {
      logger.error('Failed to parse stored credentials:', e);
    }
  }
  // No fallback - user must configure in Settings
  logger.warn('⚠️ No credentials found in localStorage');
  return {
    apiKey: '',
    appId: '',
    groupId: ''
  };
}

// 可快取的唯讀命令
// ⚠️ GenerateReport 不應該緩存，因為它需要實時數據且 API 格式可能更新
// ✅ FIX (2026-01-25): 移除 QueryStatus 緩存 - 需要即時數據，WebSocket 更新後不應返回舊數據
const CACHEABLE_COMMANDS = ['QueryLocations', 'QueryDevices'];

// Ultron Turing API Mapper request helper
async function turingRequest(command: string, params: Record<string, unknown> = {}, retryCount = 0): Promise<any> {
  // ✅ 檢查快取（只有唯讀命令才快取）
  const isCacheable = CACHEABLE_COMMANDS.includes(command);
  if (isCacheable && retryCount === 0) {
    const cached = getFromCache(command, params);
    if (cached) {
      return cached;
    }
  }
  
  const authMode = sessionStorage.getItem('auth_mode');

  // Build headers based on authentication method
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  // Check auth mode to determine priority
  // If auth_mode is 'oauth', use Bearer token authentication
  if (authMode === 'oauth') {
    // ✅ OAuth mode - use Bearer token from sessionStorage (more secure)
    const accessToken = getOAuthAccessToken();
    if (!accessToken) {
      logger.error('⚠️ No OAuth token found. Please login again.');
      return {
        success: false,
        data: [],
        message: 'Missing OAuth token. Please login again.'
      };
    }
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Ultron-Cloud-Appid'] = 'KLMGeDMy'; // Unified Boards appId

    // ✅ OAuth users don't need to provide API Key manually
    // Server automatically retrieves tenant's backend API Key from Firestore for background tasks
    // API Key is auto-generated and encrypted on first OAuth login
  }
  // Use API key if available and valid
  else {
    const credentials = getCredentials();
    // Skip known test/demo API key that was used for initial development
    // This key is invalid and should not be used for actual requests
    if (credentials.apiKey && credentials.appId &&
        credentials.apiKey !== 'e5e8fd95ad084c729e088bd062e7c398') {
      const apiKey = String(credentials.apiKey || '').trim();
      const appId = String(credentials.appId || '').trim();

      // Use API key as-is without sanitization (API keys may contain special characters)
      headers['X-Api-Key'] = apiKey;
      headers['Ultron-Cloud-Appid'] = appId;

      // ✅ 移除敏感信息日志（API Key 长度和部分内容）
      logger.log('🔑 Using API Key authentication');
      // 不再记录 API Key 的任何信息（长度、部分内容等）
    }
    // No valid authentication
    else {
      logger.error('⚠️ No valid authentication configured.');
      return {
        success: false,
        data: [],
        message: 'No authentication configured'
      };
    }
  }

  // Get API URL dynamically based on environment (may change if user switches environments)
  const apiUrl = getTuringApiUrl();
  const url = `${apiUrl}/mcp/${command}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
      credentials: 'include' // ✅ Include cookies for OAuth authentication (sessionId cookie)
    });

    // Special handling for 429 (Rate Limit Exceeded)
    if (response.status === 429) {
      logger.error('❌ API Rate Limit Exceeded (429)');

      // Trigger toast notification
      window.dispatchEvent(new CustomEvent('new-alert', {
        detail: {
          id: `rate-limit-${Date.now()}`,
          type: 'warning',
          title: '⚠️ API Rate Limit',
          message: 'Too many requests. Please wait a moment and try again.',
          timestamp: new Date().toISOString()
        }
      }));

      throw new Error('API rate limit exceeded');
    }

    const responseText = await response.text();

    // Parse response
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      logger.error(`❌ Failed to parse response: ${responseText.substring(0, 100)}...`); // 只记录前100字符
      throw new Error(`Invalid JSON response: ${responseText}`);
    }

    // Check for API-level errors even if HTTP status is 200
    // The Turing API might return 200 with error in body
    if (!response.ok || data.error || data.success === false) {
      logger.error(`❌ API Error - Status: ${response.status}, Body:`, data);

      // Special handling for OAuth token validation failures
      // Attempt automatic token refresh on 401 errors
      if (response.status === 401 && authMode === 'oauth') {
        // Only attempt refresh once to prevent infinite recursion
        if (retryCount === 0) {
          logger.warn('⚠️ 401 Unauthorized - Attempting automatic token refresh...');

          try {
            // Import authService dynamically to avoid circular dependencies
            const { authService } = await import('./authService');
            const refreshSuccess = await authService.refreshToken();

            if (refreshSuccess) {
              // Token refreshed successfully - retry the original request
              logger.log('✅ Token refreshed successfully, retrying request...');
              return turingRequest(command, params, retryCount + 1);
            }
          } catch (refreshError) {
            logger.error('❌ Token refresh failed:', refreshError);
          }
        }

        // Token refresh failed or already retried
        logger.error('❌ Authentication failed - token refresh unsuccessful');

        // Dispatch session-expired event to show login modal
        // Only dispatch once to avoid duplicate modals
        if (retryCount === 0) {
          // First attempt failed - authService should have dispatched, but ensure it happens
          logger.warn('🔐 Token refresh failed on first attempt, ensuring session-expired event is dispatched');
          // Small delay to ensure authService's event has time to dispatch first
          setTimeout(() => {
            if (!document.querySelector('[class*="SessionExpiredModal"]')) {
              logger.warn('🔐 Dispatching session-expired event (fallback)');
              window.dispatchEvent(new CustomEvent('session-expired'));
            }
          }, 100);
        } else {
          // Already retried - definitely need to show login
          logger.warn('⚠️ Already retried, clearing expired tokens and showing login');
          sessionStorage.removeItem('ultron_tokens');
          sessionStorage.removeItem('auth_mode');
          localStorage.removeItem('ultron_tokens');
          window.dispatchEvent(new CustomEvent('session-expired'));
        }

        throw new Error('Authentication failed: Unable to refresh token. Please check your connection and try again.');
      }

      // Special handling for API Key authentication errors
      if (responseText.includes('Invalid API key') ||
          (responseText.includes('Unauthorized') && authMode !== 'oauth')) {

        // Trigger warning notification for API Key issues
        window.dispatchEvent(new CustomEvent('new-alert', {
          detail: {
            id: `invalid-api-key-${Date.now()}`,
            type: 'warning',
            title: '⚠️ Invalid API Key',
            message: 'Your API Key is invalid. Please check your settings.',
            timestamp: new Date().toISOString()
          }
        }));

        throw new Error('Authentication failed: Invalid API key');
      }

      // Format error message properly - handle object errors
      const errorMsg = typeof data.error === 'object'
        ? JSON.stringify(data.error)
        : (data.error || data.message || `HTTP ${response.status}: ${response.statusText}`);
      throw new Error(errorMsg);
    }

    // Extract result from response
    // Turing API returns: { tool: 'command', result: { ... } }
    if (data.tool && data.result) {
      logger.log(`✅ Extracted result from Turing response`);
      
      // ✅ 快取成功回應
      if (isCacheable) {
        setToCache(command, params, data.result);
      }

      return data.result;
    }

    // ✅ 快取成功回應
    if (isCacheable && data.success !== false) {
      setToCache(command, params, data);
    }

    return data;
  } catch (error) {
    logger.error(`❌ Turing API Error - ${command}:`, error);
    throw error;
  }
}

export const ultronTuringApi = {

  /**
   * Query all locations/groups
   */
  async queryLocations(filter?: string): Promise<LocationResponse> {
    try {
      // Call QueryLocations via HTTP API
      const result = await turingRequest('QueryLocations', {
        filter: filter || undefined
      });

      logger.log('📍 QueryLocations result:', result);

      // Return the full result object for OAuth flow
      // The result contains { success, data: { locations, groups } }
      return result;
    } catch (error) {
      logger.error('Error querying locations:', error);
      // Return empty object with error on failure
      return { success: false, error: error };
    }
  },

  /**
   * Query devices with multiple query methods
   * @param locationId - Single location ID (legacy parameter for backward compatibility)
   * @param filter - Additional filter options
   * @param options - New query options supporting multiple query methods
   * @param options.sns - Exact device query by serial numbers
   * @param options.locationIds - Query devices across multiple locations
   * @param options.name - Fuzzy search by device name (searches sn, name, displayName, nickname)
   * @param options.detailed - Get detailed device information (default: true)
   * @param options.multiChannelDisplayModes - Per-device display mode preferences (baseSN -> 'merged' | 'expanded')
   */
  async queryDevices(
    locationId?: string,
    filter?: Record<string, unknown>,
    options?: {
      sns?: string[];
      locationIds?: string[];
      name?: string;
      detailed?: boolean;
      multiChannelDisplayModes?: Record<string, 'merged' | 'expanded'>;
    }
  ): Promise<Device[]> {
    try {
      // Only get credentials if not using OAuth
      const authMode = sessionStorage.getItem('auth_mode');
      let defaultLocationId = '';

      if (authMode !== 'oauth') {
        const credentials = getCredentials();
        defaultLocationId = credentials.groupId;
      }

      // Build request params based on query method priority:
      // 1. sns (exact device query) - highest priority
      // 2. name (fuzzy search)
      // 3. locationIds (multi-location query)
      // 4. locationId (single location - backward compatibility)
      const params: Record<string, unknown> = {
        detailed: options?.detailed !== false // Default to true
      };

      // Priority 1: Exact device query by SNs
      if (options?.sns && options.sns.length > 0) {
        params.sns = options.sns;
        logger.log('🔍 QueryDevices - Exact query by SNs:', options.sns);
      }
      // Priority 2: Fuzzy name search
      else if (options?.name) {
        params.name = options.name;
        logger.log('🔍 QueryDevices - Fuzzy search by name:', options.name);
      }
      // Priority 3: Multi-location query
      else if (options?.locationIds && options.locationIds.length > 0) {
        params.locationIds = options.locationIds;
        logger.log('🔍 QueryDevices - Multi-location query:', options.locationIds);
      }
      // Priority 4: Single location (backward compatibility)
      else if (locationId || defaultLocationId) {
        params.locationId = locationId || defaultLocationId;
        logger.log('🔍 QueryDevices - Single location query:', params.locationId);
      }

      // Add legacy filter if provided
      if (filter) {
        params.filter = filter;
      }

      const result = await turingRequest('QueryDevices', params);
      logger.log('🔌 Devices result:', result);
      logger.log('🔌 Result type:', typeof result);
      logger.log('🔌 Result keys:', Object.keys(result || {}));

      // Handle different response formats from Turing API
      // The API returns { success: true, type: 'devices', data: [...], count: N }
      let devices: any[] = [];

      if (result.success && result.data && Array.isArray(result.data)) {
        devices = result.data;
      } else if (result.data && Array.isArray(result.data)) {
        devices = result.data;
      } else if (result.devices && Array.isArray(result.devices)) {
        devices = result.devices;
      } else if (Array.isArray(result)) {
        devices = result;
      }

      if (devices.length > 0) {
        // Transform API response to normalize fields
        // Use flatMap to handle multi-capability devices (new format: system + capabilities)
        const transformedDevices = devices.flatMap((d: any) => {
          // Detect NEW format: { system: {...}, capabilities: [...] }
          if (d.system && Array.isArray(d.capabilities)) {
            const { system, capabilities } = d;
            
            // Determine online status - check multiple possible locations and property names
            const isOnline = d.online ?? d.isOnline ?? d.connected ?? system.online ?? system.isOnline ?? system.connected ?? false;
            
            // ✅ NEW: Use endpointRelationship to determine how to display
            const endpointRelationship = system.endpointRelationship || {};
            const classification = endpointRelationship.classification || 'single';
            const independentEndpoints = endpointRelationship.independentEndpoints ?? false;
            
            // Try to determine baseSN from multiple sources
            let baseSN = system.baseSN || system.sn;
            if (!baseSN && capabilities[0]?.deviceSN) {
              baseSN = capabilities[0].deviceSN.split('#')[0];
            }
            
            if (!baseSN) {
              logger.warn('⚠️ Skipping device with no valid identifier:', { system });
              return [];
            }
            
            // ✅ Check user preference for multi_channel devices
            const displayModes = options?.multiChannelDisplayModes || {};
            // Default: multi_channel = 'expanded' (split), single/composite = always merged
            const userDisplayMode = displayModes[baseSN] || 'expanded';
            
            // ✅ 檢查是否有多個 power meter endpoints（需要展開顯示各自能耗）
            const powerMeterEndpoints = capabilities.filter((cap: any) => {
              const sp = cap.attributes?.sensorProfile || {};
              return sp.watt || sp.watt_hour || sp.real_power || sp.voltage || sp.current;
            });
            const hasMultiplePowerMeters = powerMeterEndpoints.length > 1;
            
            // ✅ For single/composite: Always create ONE merged device
            // For multi_channel: Check user preference (default: expanded/split)
            // ⚠️ EXCEPTION: 如果有多個 power meter endpoints，必須展開以便分別顯示能耗
            const shouldMerge = 
              !hasMultiplePowerMeters && (
                (classification === 'single' || classification === 'composite') ||
                (classification === 'multi_channel' && userDisplayMode === 'merged')
              );
            
            if (shouldMerge && !independentEndpoints) {
              // ✅ FIX: Use primaryEndpoint from endpointRelationship if available
              // Otherwise fallback to first capability with non-null type
              const primaryEndpointName = endpointRelationship.primaryEndpoint;
              const primaryCap = primaryEndpointName
                ? capabilities.find((c: any) => c.endpoint === primaryEndpointName || c.type === primaryEndpointName)
                : capabilities.find((c: any) => c.type !== null) || capabilities[0];
              
              // Merge all sensorProfiles from all capabilities
              const mergedSensorProfile: Record<string, any> = {};
              capabilities.forEach((cap: any) => {
                const sp = cap.attributes?.sensorProfile || {};
                Object.assign(mergedSensorProfile, sp);
              });
              
              // Collect all capability types for badges
              const allCapabilityTypes = capabilities
                .map((c: any) => c.type)
                .filter(Boolean);
              
              // Collect all traits from all capabilities
              const allTraits = Array.from(new Set(capabilities.flatMap((c: any) => c.traits || [])));
              
              // ✅ For multi_channel merged mode: Store channels for expandable UI
              const channels = classification === 'multi_channel' ? capabilities.map((cap: any) => ({
                sn: cap.deviceSN || (cap.endpoint ? `${baseSN}#${cap.endpoint}` : baseSN),
                endpoint: cap.endpoint,
                nickname: cap.nickname,
                displayName: cap.displayName,
                type: cap.type,
                traits: cap.traits || [],
              })) : undefined;
              
              // ✅ FIX: 對於 single/composite 設備，使用 primaryCap.deviceSN 作為完整的 deviceSN
              // 這樣 GenerateReport API 才能正確返回 totalConsumption
              const fullDeviceSN = primaryCap?.deviceSN || `${baseSN}#${primaryCap?.endpoint || '1'}`;
              
              logger.log(`🔗 [MERGE] ${baseSN}: classification=${classification}, fullDeviceSN=${fullDeviceSN}, capabilities=${allCapabilityTypes.join(',')}${channels ? `, channels=${channels.length}` : ''}`);
              
              // ✅ Extract customControls and availableCommands
              // customControls is at device level, availableCommands is in each capability's attributes
              const customControls = d.customControls || {};
              const availableCommands = primaryCap?.attributes?.availableCommands;
              
              // For merged devices, extract customControls for the primary endpoint
              const endpointKey = primaryCap?.endpoint || '1';
              const endpointCustomControls = customControls[endpointKey] || customControls[primaryCap?.endpoint] || {};
              
              return [{
                sn: fullDeviceSN,
                deviceSN: fullDeviceSN,
                baseSN: baseSN,
                endpoint: primaryCap?.endpoint,
                name: primaryCap?.nickname || system.deviceName || system.name || baseSN,
                displayName: primaryCap?.nickname || primaryCap?.displayName || system.deviceName || system.displayName,
                nickname: primaryCap?.nickname || system.nickname,
                // ✅ Preserve system.deviceName for merged multi-endpoint display title
                systemDeviceName: system.deviceName,
                online: isOnline,
                location: system.location,
                traits: allTraits,
                type: primaryCap?.type,
                // ✅ Store all capability types for badge display
                capabilityTypes: allCapabilityTypes,
                // ✅ For multi_channel merged mode: Store channels for expandable UI
                channels: channels,
                // Organization
                groupId: system.groupId,
                groupName: system.groupName,
                _source: d._source,
                attributes: primaryCap?.attributes,
                sensorProfile: mergedSensorProfile,
                // ✅ Store endpointRelationship for reference
                endpointRelationship: endpointRelationship,
                // ✅ NEW: Add customControls and availableCommands
                customControls: endpointCustomControls ? { [endpointKey]: endpointCustomControls } : undefined,
                availableCommands: availableCommands,
              }];
            }
            
            // ✅ For multi_channel/independent_devices: Create SEPARATE devices for each capability
            logger.log(`📤 [SPLIT] ${baseSN}: classification=${classification}, creating ${capabilities.length} separate devices`);
            
            // ✅ Extract customControls from device level
            const customControls = d.customControls || {};
            
            return capabilities.map((cap: any) => {
              const deviceSN = cap.deviceSN || (cap.endpoint ? `${baseSN}#${cap.endpoint}` : baseSN);
              const sensorProfile = cap.attributes?.sensorProfile || {};
              const capOnline = cap.online ?? cap.isOnline ?? isOnline;
              
              // ✅ Extract customControls and availableCommands for this endpoint
              const endpointKey = cap.endpoint || '1';
              const endpointCustomControls = customControls[endpointKey] || customControls[cap.endpoint] || {};
              const availableCommands = cap.attributes?.availableCommands;
              
              return {
                sn: deviceSN,
                deviceSN: deviceSN,
                baseSN: baseSN,
                endpoint: cap.endpoint,
                name: cap.nickname || system.deviceName || system.name || deviceSN,
                displayName: cap.nickname || cap.displayName || system.deviceName || system.displayName,
                nickname: cap.nickname || system.nickname,
                // ✅ Preserve system.deviceName for merged multi-endpoint display title
                systemDeviceName: system.deviceName,
                online: capOnline,
                location: system.location,
                traits: cap.traits || [],
                type: cap.type,
                groupId: system.groupId,
                groupName: system.groupName,
                _source: d._source,
                detectedCapabilities: cap.detectedCapabilities,
                attributes: cap.attributes,
                sensorProfile: sensorProfile,
                endpointRelationship: endpointRelationship,
                // ✅ NEW: Add customControls and availableCommands
                customControls: endpointCustomControls && Object.keys(endpointCustomControls).length > 0 
                  ? { [endpointKey]: endpointCustomControls } 
                  : undefined,
                availableCommands: availableCommands,
              };
            }).filter(Boolean);
          }
          
          // OLD format: flat device object
          // Check multiple possible property names for online status
          const oldFormatOnline = d.online ?? d.isOnline ?? d.connected ?? false;
          
          return [{
            // Use deviceSN as primary identifier, fallback to baseSN or sn
            sn: d.deviceSN || d.baseSN || d.sn,
            deviceSN: d.deviceSN,
            baseSN: d.baseSN,
            endpoint: d.endpoint,
            // Display names
            name: d.name || d.displayName || d.deviceSN,
            displayName: d.displayName,
            nickname: d.nickname,
            // Metadata
            online: oldFormatOnline,
            location: d.location,
            traits: d.traits || [],
            type: d.type,
            // Organization
            groupId: d.groupId,
            groupName: d.groupName,
            // API metadata
            _source: d._source,
            detectedCapabilities: d.detectedCapabilities,
            attributes: d.attributes,
            // Normalized sensorProfile for backward compatibility
            sensorProfile: d.attributes?.sensorProfile || d.sensorProfile || {},
            // ✅ NEW: Add customControls and availableCommands for old format
            customControls: d.customControls,
            availableCommands: d.attributes?.availableCommands,
          }];
        });

        logger.log(`✅ Found ${transformedDevices.length} devices from Turing API`);
        return transformedDevices;
      }

      logger.warn('⚠️ No devices found in response', result);
      return [];
    } catch (error) {
      logger.error('QueryDevices error:', error);
      return [];
    }
  },

  /**
   * Query device status with multiple query methods
   * @param deviceIds - Specific device IDs to query (legacy parameter for backward compatibility)
   * @param detailed - Get detailed status information (default: false)
   * @param options - New query options supporting multiple query methods
   * @param options.locationId - Single location ID (for backward compatibility)
   * @param options.locationIds - Query status across multiple locations
   * @param options.name - Fuzzy search by device name
   */
  async queryStatus(
    deviceIds?: string[],
    options?: {
      locationId?: string;
      locationIds?: string[];
      name?: string;
      detailed?: boolean;
    }
  ): Promise<Record<string, any>> {
    try {
      const authMode = sessionStorage.getItem('auth_mode');
      const params: Record<string, unknown> = {};


      // Build request params based on query method priority:
      // 1. devices (exact device list) - highest priority
      // 2. name (fuzzy search)
      // 3. locationIds (multi-location query)
      // 4. locationId (single location)

      // Priority 1: Specific devices
      // Strip #N suffix and deduplicate - API requires baseSN format
      if (deviceIds && deviceIds.length > 0) {
        const baseSNs = Array.from(new Set(deviceIds.map(id => id.split('#')[0])));
        params.devices = baseSNs;
        logger.log(`📊 QueryStatus: Converted ${deviceIds.length} device IDs to ${baseSNs.length} baseSNs`);
      }
      // Priority 2: Fuzzy name search
      else if (options?.name) {
        params.name = options.name;
      }
      // Priority 3: Multi-location query
      else if (options?.locationIds && options.locationIds.length > 0) {
        params.locationIds = options.locationIds;
      }
      // Priority 4: Single location
      else if (options?.locationId) {
        params.locationId = options.locationId;
      }
      // Fallback: Add locationId for API Key mode only
      else if (authMode !== 'oauth') {
        const credentials = getCredentials();
        if (credentials.groupId) {
          params.locationId = credentials.groupId;
        }
      }
      // For OAuth mode without specific params, don't provide locationId - API will query across all user's groups

      // Add detailed parameter if provided
      if (options?.detailed !== undefined) {
        params.detailed = options.detailed;
      }

      const result = await turingRequest('QueryStatus', params);

      return result;
    } catch (error) {
      logger.error('QueryStatus error:', error);
      return {};
    }
  },

  /**
   * Execute device command
   * Supports both new simplified commands and legacy Google Home commands
   * 
   * New simplified commands (recommended):
   * - "on", "off", "toggle" - Device power control
   * - "setBrightness" - Brightness control (0-100)
   * - "setColorTemperature" - Color temperature (2000-9000K)
   * - "setColor" - RGB color control
   * - "setTemperature" - HVAC temperature (19-30°C)
   * - "setMode" - HVAC mode (cool, heat, dry, fan-only)
   * - "setFanSpeed" - Fan speed (auto, low, medium, high)
   * 
   * Legacy commands (still supported):
   * - "OnOff", "BrightnessAbsolute", "ColorAbsolute", etc.
   * 
   * Note: ExecuteCommand automatically finds device location, groupId is optional
   */
  async executeCommand(command: string, devices: string[], params?: CommandParams): Promise<CommandResponse> {
    // Normalize command params for new simplified commands
    const normalizedParams = { ...params };
    
    // Map legacy colorTemp to new temperature format for setColorTemperature
    if (command === 'setColorTemperature' && normalizedParams?.colorTemp && !normalizedParams?.temperature) {
      normalizedParams.temperature = normalizedParams.colorTemp;
      delete normalizedParams.colorTemp;
    }
    
    const result = await turingRequest('ExecuteCommand', {
      command,
      devices,
      params: normalizedParams || {}
    });
    logger.log('🎯 Command executed:', result);
    return result;
  },

  /**
   * Sync Shadow State - Force sync device states from UltronCloud
   * This triggers the backend to refresh device states including online status
   */
  async syncShadowState(force: boolean = true): Promise<any> {
    const result = await turingRequest('SyncShadowState', { force });
    logger.log('🔄 Shadow state synced:', result);
    return result;
  },

  /**
   * Sync Capabilities - Force sync device list/capabilities from UltronCloud
   * This triggers the backend to refresh the device list (new devices will appear)
   */
  async syncCapabilities(groupId?: string): Promise<any> {
    const params: any = {};
    if (groupId) params.groupId = groupId;
    const result = await turingRequest('SyncCapabilities', params);
    logger.log('🔄 Capabilities synced:', result);
    return result;
  },

  /**
   * Configure automation rules
   */
  async configureAutomation(config: {
    name: string;
    trigger: AutomationTrigger;
    actions: AutomationAction[];
    enabled?: boolean;
  }): Promise<ApiResponse<unknown>> {
    const result = await turingRequest('ConfigureAutomation', {
      name: config.name,
      trigger: config.trigger,
      actions: config.actions,
      enabled: config.enabled !== false
    });
    logger.log('🤖 Automation configured:', result);
    return result;
  },

  /**
   * Generate report - 生成能源報告
   * 使用新的 MCP GenerateReport 格式
   * @param deviceSNs - 設備 SN 列表（必填）
   * @param options - 可選參數
   * @param options.duration - 時間長度，如 "3d" (3天), "24h" (24小時), "7d" (7天) 等
   * @param options.endTime - ISO 8601 格式結束時間（可選，不傳時使用當前時間）
   * @param options.granularity - 數據粒度: minute, hour, day, auto (默認 auto)
   * @param options.metrics - 指標列表或 'auto' (默認 'auto')
   * @param options.format - 報告格式: summary, detailed (默認 summary)
   * @param options.utcOffset - 時區偏移，如 "+8:00" (默認 "+8:00" 台北時間)
   */
  async generateReport(
    deviceSNs: string[],
    options?: {
      day?: number;  // 天数，如 4
      // 向后兼容的旧参数（已废弃，但保留以避免破坏现有代码）
      duration?: string;
      endTime?: string;
      granularity?: 'minute' | 'hour' | 'day' | 'auto';
      metrics?: string[] | 'auto';
      format?: 'summary' | 'detailed';
      utcOffset?: string;
      // ✅ 新增：快取控制選項
      skipCache?: boolean;  // 強制跳過快取
      cacheTtlMs?: number;  // 自定義快取時效（毫秒）
    }
  ): Promise<ReportResponse> {
    logger.log('📊 GenerateReport called', { deviceSNs, options });
    
    // 驗證必填參數
    if (!deviceSNs || deviceSNs.length === 0) {
      logger.error('❌ GenerateReport: deviceSNs 參數為必填！');
      return {
        success: false,
        error: 'deviceSNs parameter is required'
      };
    }

    const duration = options?.duration || `${options?.day || 1}d`;
    const granularity = options?.granularity || 'auto';
    const utcOffset = options?.utcOffset || '+8:00';

    console.log(`📊 [GenerateReport] Checking cache: duration=${duration}, granularity=${granularity}, devices=${deviceSNs.length}`);

    // ✅ 檢查本地快取（minute 粒度使用較短的 TTL）
    if (!options?.skipCache) {
      // minute 粒度使用 3 分鐘 TTL，其他使用默認 TTL
      const cacheTtl = granularity === 'minute' ? 3 * 60 * 1000 : options?.cacheTtlMs;
      console.log(`📊 [GenerateReport] Will check cache (granularity=${granularity}, ttl=${cacheTtl ? cacheTtl/1000 + 's' : 'default'})`);
      const cached = reportCacheService.getCachedReport(
        deviceSNs,
        duration,
        granularity,
        utcOffset,
        cacheTtl ? { ttlMs: cacheTtl } : undefined
      );
      
      if (cached && !cached.isStale) {
        console.log('📦 [GenerateReport] ✅ Using cached data (fresh) - SKIPPING API CALL ⚡');
        console.log('📦 [GenerateReport] Cached data keys:', cached.data ? Object.keys(cached.data) : 'null');
        return cached.data;
      }
      
      if (cached) {
        console.log('📦 [GenerateReport] Cache found but stale, isStale:', cached.isStale);
      }
      
      // 如果快取過期但存在，可以先返回舊數據，同時背景更新
      // 這裡選擇簡單策略：過期就重新請求
      if (cached && cached.isStale) {
        console.log('📦 [GenerateReport] ⚠️ Cache is stale, will fetch fresh data');
      }
      
      if (!cached) {
        console.log('📦 [GenerateReport] ❌ No cache found, will fetch from API');
      }
    } else {
      console.log(`📊 [GenerateReport] Skipping cache check (skipCache=${options?.skipCache}, granularity=${granularity})`);
    }

    // ✅ 构建参数：支持 day 或 duration/granularity/format 格式
    const params: Record<string, unknown> = {
      deviceSNs: deviceSNs
    };
    
    // 如果提供了 day，使用简单格式
    if (options?.day !== undefined) {
      params.day = options.day;
    } else {
      // 否则使用详细格式
      if (options?.duration) {
        params.duration = options.duration;
      }
      if (options?.granularity) {
        params.granularity = options.granularity;
      }
      if (options?.format) {
        params.format = options.format;
      }
      if (options?.endTime) {
        params.endTime = options.endTime;
      }
      if (options?.metrics) {
        params.metrics = options.metrics;
      }
      if (options?.utcOffset) {
        params.utcOffset = options.utcOffset;
      }
    }

    console.log('🌐 [GenerateReport] MAKING API CALL - params:', { duration, granularity, deviceCount: deviceSNs.length });
    
    try {
      const result = await turingRequest('GenerateReport', params);
      logger.log('📈 Report:', result);
      
      // ✅ 儲存到本地快取（所有粒度，minute 使用較短 TTL）
      if (result.success) {
        reportCacheService.saveReport(
          deviceSNs,
          duration,
          granularity,
          result,
          utcOffset
        );
      }
      
      return result;
    } catch (error) {
      logger.error('❌ GenerateReport failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
        data: {}
      };
    }
  },

  // NOTE: generateHourlyChartData has been removed as it's no longer used.
  // All chart data is now fetched directly using generateReport API in IntelliSpaceDashboard.tsx

  /**
   * @deprecated This function is no longer used and will be removed in a future release.
   * All chart data is now fetched directly using generateReport API in IntelliSpaceDashboard.tsx
   * Generate hourly chart data for a specific day (IntelliSpace Overview)
   * Returns hourly power consumption and climate data
   * @param deviceIds - Array of device IDs to query (必填)
   * @param dayOffset - 0 for today, -1 for yesterday, etc. (default: 0)
   */
  async generateHourlyChartData(deviceIds?: string[], dayOffset: number = 0): Promise<{
    powerData: Array<{hour: string, power: number, energy: number}>;
    climateData: Array<{hour: string, temp: number, humidity: number}>;
    totalEnergy?: number; // ✅ 新增：返回後端計算的精確總用電量
  }> {
    const now = new Date();
    const targetDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + dayOffset);
    const dayStart = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 0, 0, 0);
    // ✅ 如果查詢的是今天（dayOffset === 0），結束時間應該是當前時間，而不是當天的 23:59:59
    // 這樣可以確保獲取的是"今天"的數據，而不是"昨天"的數據
    const dayEnd = dayOffset === 0 ? now : new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 23, 59, 59);
    
    // 分離電錶設備和溫濕度計設備
    // ✅ Energy 報告需要電錶設備 (UT3M 系列 + UTU500 三相電錶)
    // ✅ Sensor 報告只需要溫濕度計設備 (UTU307, UTU308, UTU31E, UTU31G 系列)
    const powerMeterPrefixes = ['UT3M', 'UTU500'];
    const thSensorPrefixes = ['UTU307', 'UTU308', 'UTU31E', 'UTU31G'];
    
    const powerMeterIds = deviceIds?.filter(id => {
      const baseSN = id.split('#')[0];
      return powerMeterPrefixes.some(prefix => baseSN.startsWith(prefix));
    }) || [];
    
    // ⚠️ 修正：保留完整的設備 ID（包含 #ULTRON_SENSOR 後綴），與 GenerateReportPage.tsx 一致
    // 之前的代碼錯誤地去掉了後綴，但 API 可能需要完整 ID 才能返回數據
    const thSensorIds = deviceIds?.filter(id => {
      const baseSN = id.split('#')[0];
      return thSensorPrefixes.some(prefix => baseSN.startsWith(prefix));
    }) || [];

    logger.log(`📊 [HourlyChartData] Starting query`, {
      dayOffset,
      deviceIds: deviceIds?.length || 0,
      powerMeterIds: powerMeterIds.length,
      thSensorIds: thSensorIds.length
    });
    

    // 檢查是否有設備 ID
    if ((!deviceIds || deviceIds.length === 0) && powerMeterIds.length === 0 && thSensorIds.length === 0) {
      logger.warn('⚠️ [HourlyChartData] No deviceIds provided, returning empty data');
      return {
        powerData: Array.from({ length: 24 }, (_, i) => ({
          hour: `${i.toString().padStart(2, '0')}:00`,
          power: 0,
          energy: 0
        })),
        climateData: []
      };
    }

    try {
      // 使用新的 generateReport API 調用方式
      // ✅ 只傳入電錶設備到 energy 報告，避免混合設備類型導致的問題
      // ✅ 即使 dayOffset === 0，也明確指定時間範圍（從今天 00:00 到現在）
      // 這樣可以確保獲取的是"今天"的數據，而不是"昨天"的數據
      const endTime = dayOffset === 0 ? now.toISOString() : dayEnd.toISOString();
      const startTime = dayStart.toISOString();
      // 計算 duration（小時數）
      const hoursDiff = Math.ceil((new Date(endTime).getTime() - new Date(startTime).getTime()) / (1000 * 60 * 60));
      const duration = `${hoursDiff}h`;
      
      const result = powerMeterIds.length > 0 
        ? await this.generateReport(powerMeterIds, {
            duration,
            endTime,
            granularity: 'hour',
            format: 'summary',  // ✅ 使用 summary 格式以獲取 totalConsumption
            utcOffset: '+8:00'
          })
        : { success: true, data: { chartData: [] } };
      
      // ✅ 完整打印 API 返回結果，用於調試
      logger.log(`📊 [HourlyChartData] RAW API result:`, JSON.stringify(result, null, 2));
      logger.log(`📊 [HourlyChartData] API result structure:`, {
        hasResult: !!result,
        resultKeys: result ? Object.keys(result) : [],
        hasData: !!result?.data,
        dataKeys: result?.data ? Object.keys(result.data) : [],
        chartDataLength: result?.data?.chartData?.length || 0,
        devicesCount: result?.data?.devices ? Object.keys(result.data.devices).length : 0,
        totalConsumption: result?.data?.totalConsumption,
        firstChartPoint: result?.data?.chartData?.[0]
      });
      

      // ✅ 處理 MCP GenerateReport 響應格式
      // 格式1: { success: true, data: { chartData: [...], totalConsumption: 29.68 } } - 直接返回
      // 格式2: { success: true, data: { "UTU500-4J2222M#1": { chartData: [...], totalConsumption: 29.68 } } } - 以設備 SN 為鍵
      const reportData = result?.data || {};
      
      logger.log(`🔍 [HourlyChartData] Processing API response...`);
      logger.log(`🔍 [HourlyChartData] reportData keys:`, Object.keys(reportData));
      logger.log(`🔍 [HourlyChartData] reportData structure (first 1000 chars):`, JSON.stringify(reportData, null, 2).substring(0, 1000));
      
      // ✅ 第一步：提取 totalConsumption（優先處理，獨立於 chartData）
      // 需要處理兩種格式：
      // 格式1: { data: { totalConsumption: 29.68, chartData: [...] } } - 根級別
      // 格式2: { data: { "UTU500-4J2222M#1": { totalConsumption: 29.68, chartData: [...] } } } - 設備對象內
      let totalEnergyFromApi = 0;
      let foundTotalConsumption = false; // 標誌：是否找到了 totalConsumption 字段
      
      
      // 識別設備鍵（排除已知的元數據鍵）
      const knownMetadataKeys = ['devices', 'totalConsumption', 'averagePower', 'peakPower', 'averageVoltage', 'averageCurrent', 'deviceType', 'availableMetrics', 'returnedMetrics', 'dataPoints'];
      const deviceKeys = Object.keys(reportData).filter(key => {
        // 排除已知的元數據鍵
        if (knownMetadataKeys.includes(key)) {
          return false;
        }
        // 只保留對象類型的鍵（可能是設備對象）
        const value = reportData[key];
        return typeof value === 'object' && value !== null && !Array.isArray(value);
      });
      
      logger.log(`🔍 [HourlyChartData] Identified ${deviceKeys.length} potential device keys:`, deviceKeys);
      
      // 方法1: 檢查根級別的 totalConsumption
      if (reportData.totalConsumption !== undefined && reportData.totalConsumption !== null) {
        totalEnergyFromApi = typeof reportData.totalConsumption === 'number' 
          ? reportData.totalConsumption 
          : parseFloat(String(reportData.totalConsumption).replace(/[^\d.]/g, '')) || 0;
        foundTotalConsumption = true;
        logger.log(`✅ [HourlyChartData] Got totalConsumption from root: ${totalEnergyFromApi} kWh`);
      } 
      // 方法2: 累加所有設備的 totalConsumption（當響應格式是以設備 SN 為鍵時）
      else if (deviceKeys.length > 0) {
        logger.log(`🔍 [HourlyChartData] Checking device objects for totalConsumption...`);
        deviceKeys.forEach(deviceKey => {
          const deviceData = reportData[deviceKey] as any;
          if (deviceData && typeof deviceData === 'object') {
            // 檢查設備對象中的 totalConsumption
            if (deviceData.totalConsumption !== undefined && deviceData.totalConsumption !== null) {
              const deviceTotal = typeof deviceData.totalConsumption === 'number'
                ? deviceData.totalConsumption
                : parseFloat(String(deviceData.totalConsumption).replace(/[^\d.]/g, '')) || 0;
              totalEnergyFromApi += deviceTotal;
              foundTotalConsumption = true;
              logger.log(`✅ [HourlyChartData] Device ${deviceKey} totalConsumption: ${deviceTotal} kWh`);
            }
          }
        });
        if (foundTotalConsumption) {
          logger.log(`✅ [HourlyChartData] Sum of all devices totalConsumption: ${totalEnergyFromApi} kWh`);
        } else {
          logger.warn(`⚠️ [HourlyChartData] No totalConsumption found in any device object`);
        }
      }
      // 方法3: 檢查 devices 對象（如果存在）
      if (!foundTotalConsumption && reportData.devices && typeof reportData.devices === 'object') {
        logger.log(`🔍 [HourlyChartData] Checking devices object for totalConsumption...`);
        Object.values(reportData.devices as Record<string, any>).forEach((device: any) => {
          if (device && typeof device === 'object' && device.totalConsumption !== undefined && device.totalConsumption !== null) {
            const deviceTotal = typeof device.totalConsumption === 'number'
              ? device.totalConsumption
              : parseFloat(String(device.totalConsumption).replace(/[^\d.]/g, '')) || 0;
            totalEnergyFromApi += deviceTotal;
            foundTotalConsumption = true;
            logger.log(`✅ [HourlyChartData] Found totalConsumption in devices object: ${deviceTotal} kWh`);
          }
        });
        if (foundTotalConsumption) {
          logger.log(`✅ [HourlyChartData] Sum from devices object: ${totalEnergyFromApi} kWh`);
        }
      }
      
      if (!foundTotalConsumption) {
        logger.warn(`⚠️ [HourlyChartData] No totalConsumption found in API response. Keys: ${Object.keys(reportData).join(', ')}`);
      }
      
      // ✅ 第二步：提取 chartData（獨立處理）
      let chartDataArray: any[] = [];
      
      logger.log(`🔍 [HourlyChartData] Extracting chartData from response...`);
      
      // 檢查是否有直接的 chartData
      if (reportData.chartData && Array.isArray(reportData.chartData)) {
        chartDataArray = reportData.chartData;
        logger.log(`✅ [HourlyChartData] Found direct chartData array with ${chartDataArray.length} items`);
      } else if (deviceKeys.length > 0) {
        // 檢查是否是以設備 SN 為鍵的格式
        logger.log(`🔍 [HourlyChartData] Extracting chartData from ${deviceKeys.length} device objects...`);
        deviceKeys.forEach(deviceKey => {
          const deviceData = reportData[deviceKey] as any;
          if (deviceData?.chartData && Array.isArray(deviceData.chartData)) {
            logger.log(`✅ [HourlyChartData] Device ${deviceKey} has chartData with ${deviceData.chartData.length} items`);
            chartDataArray = chartDataArray.concat(deviceData.chartData);
          }
        });
        logger.log(`✅ [HourlyChartData] Merged chartData array has ${chartDataArray.length} total items`);
      }
      
      // 如果還是沒有數據，打印第一個數據點以便調試
      if (chartDataArray.length > 0) {
        logger.log(`📊 [HourlyChartData] Sample chartData point:`, chartDataArray[0]);
      } else {
        logger.warn(`⚠️ [HourlyChartData] No chartData found in response`);
      }

    // Process power data from chartData
      // ✅ 使用 Map 來合併同一小時的數據（MCP 返回的數據每個小時可能有多條）
      const hourlyDataMap = new Map<string, { power: number, energy: number, hasPeriodEnergy: boolean }>();
      
      logger.log(`🔄 [HourlyChartData] Processing ${chartDataArray.length} data points...`);
      
      if (chartDataArray.length > 0) {
        chartDataArray.forEach((point: any, index: number) => {
          const time = point.time || point.timestamp;
          if (time) {
            const date = new Date(time);
            const hour = `${date.getHours().toString().padStart(2, '0')}:00`;
            
            let pointPower = 0;
            let pointEnergy = 0;
            let hasPeriodEnergy = false;
            
            // ✅ MCP 已統一輸出 energy 欄位 = 該時段用電量 (kWh)
            // FIX (2025-12-31): 前端只需使用 energy 字段，無需區分設備類型
            // - UT3M15: MCP 內部用 periodEnergy 計算，輸出為 energy
            // - UTU500: MCP 內部用 energy，輸出也是 energy
            // ✅ 同時檢查 periodEnergy 作為 fallback（某些情況下 MCP 可能仍返回 periodEnergy）
            if (point.avgPower !== undefined || point.energy !== undefined || point.periodEnergy !== undefined) {
              pointPower = typeof point.avgPower === 'number' ? point.avgPower : 0;
              
              // 優先使用 energy，如果沒有則使用 periodEnergy
              if (point.energy !== undefined) {
                pointEnergy = typeof point.energy === 'number' ? point.energy : parseFloat(point.energy);
                hasPeriodEnergy = true;
              } else if (point.periodEnergy !== undefined) {
                pointEnergy = typeof point.periodEnergy === 'number' ? point.periodEnergy : parseFloat(point.periodEnergy);
                hasPeriodEnergy = true;
              } else {
                // ⚠️ Fallback: 如果沒有 energy 數據，使用平均功率估算 (W / 1000 = kWh)
                // 這是針對那些只上報功率的設備，或者 MCP 尚未計算出差值的情況
                pointEnergy = pointPower / 1000;
              }
              
              // 打印前3個數據點的詳細信息
              if (index < 3) {
                logger.log(`📊 [HourlyChartData] Point ${index} (${hour}):`, {
                  avgPower: point.avgPower,
                  energy: point.energy,
                  periodEnergy: point.periodEnergy,
                  extractedPower: pointPower,
                  extractedEnergy: pointEnergy,
                  isFallback: !hasPeriodEnergy
                });
              }
            }
            // 舊格式兼容：endpoints 對象
            else if (point.endpoints && typeof point.endpoints === 'object') {
              if (deviceIds && deviceIds.length > 0) {
                deviceIds.forEach(deviceId => {
                  const endpointPower = point.endpoints[deviceId] || 0;
                  pointPower += endpointPower;
                  pointEnergy += endpointPower / 1000;
                });
              } else {
                Object.values(point.endpoints).forEach((endpointPower: any) => {
                  const power = typeof endpointPower === 'number' ? endpointPower : 0;
                  pointPower += power;
                  pointEnergy += power / 1000;
                });
              }
            }
            // 舊格式兼容：直接 power 欄位
            else if (point.power !== undefined) {
              pointPower = point.power || 0;
              pointEnergy = pointPower / 1000;
            }
            // 最後 fallback
            else {
              Object.keys(point).forEach(key => {
                if (key !== 'time' && key !== 'timestamp' && key !== 'endpoints' && key !== 'deviceCount') {
                  const deviceData = point[key];
                  if (typeof deviceData === 'object') {
                    pointPower += deviceData.avgPower || deviceData.watt || deviceData.power || 0;
                    // ✅ Fix: watt_hour is in Wh, divide by 1000 to convert to kWh
                    // 優先使用 energy，如果沒有則使用 periodEnergy，最後 fallback 到 watt_hour
                    pointEnergy += deviceData.energy || deviceData.periodEnergy || (deviceData.watt_hour ? deviceData.watt_hour / 1000 : 0);
                    if (deviceData.energy !== undefined || deviceData.periodEnergy !== undefined) {
                      hasPeriodEnergy = true;
                    }
                  } else if (typeof deviceData === 'number') {
                    pointPower += deviceData;
                    pointEnergy += deviceData / 1000;
                  }
                }
              });
            }
            
            // ✅ 合併同一小時的數據：
            // 由於 chartDataArray 可能包含多個設備的數據（當響應是以設備 SN 為鍵時），
            // 同一小時的多個數據點代表不同設備的數據，因此必須累加 (Sum) 而不是取最大值。
            const existing = hourlyDataMap.get(hour);
            if (!existing) {
              hourlyDataMap.set(hour, { power: pointPower, energy: pointEnergy, hasPeriodEnergy });
            } else {
              // 累加功率和能耗
              hourlyDataMap.set(hour, { 
                power: existing.power + pointPower, 
                energy: existing.energy + pointEnergy,
                hasPeriodEnergy: existing.hasPeriodEnergy || hasPeriodEnergy
              });
            }
          }
        });
      }
      
      // 轉換 Map 為 Array
      const powerData: Array<{hour: string, power: number, energy: number}> = [];
      hourlyDataMap.forEach((data, hour) => {
        powerData.push({ hour, power: Math.round(data.power), energy: parseFloat(data.energy.toFixed(3)) });
      });

      // If no chartData, try to construct from devices data
      if (powerData.length === 0 && result?.data?.devices) {
        const currentHour = now.getHours();
        for (let i = 0; i <= currentHour; i++) {
          const hour = `${i.toString().padStart(2, '0')}:00`;
          let totalPower = 0;
          let totalEnergy = 0;
          
          Object.values(result.data.devices).forEach((device: any) => {
            if (device.dataPoints && Array.isArray(device.dataPoints)) {
              const hourPoint = device.dataPoints.find((p: any) => {
                const pointHour = new Date(p.time || p.timestamp).getHours();
                return pointHour === i;
              });
              if (hourPoint) {
                totalPower += hourPoint.watt || hourPoint.power || 0;
                // ✅ Fix: watt_hour is in Wh, divide by 1000 to convert to kWh
                totalEnergy += hourPoint.energy || (hourPoint.watt_hour ? hourPoint.watt_hour / 1000 : 0);
              }
            }
          });
          
          // energy 保留小數精度，power 使用整數
          powerData.push({ hour, power: Math.round(totalPower), energy: parseFloat(totalEnergy.toFixed(3)) });
        }
      }

      // Fill missing hours with 0
      const filledPowerData: Array<{hour: string, power: number, energy: number}> = [];
      const currentHour = now.getHours();
      for (let i = 0; i < 24; i++) {
        const hour = `${i.toString().padStart(2, '0')}:00`;
        const existing = powerData.find(p => p.hour === hour);
        if (existing) {
          filledPowerData.push(existing);
        } else {
          filledPowerData.push({ hour, power: i <= currentHour ? 0 : 0, energy: 0 });
        }
      }

      logger.log('📊 [HourlyChartData] Processed power data:', filledPowerData);

      // For climate data, we need sensor report (same as ClimateDetailModal)
      const climateData: Array<{hour: string, temp: number, humidity: number}> = [];
      
      try {
        // ✅ 使用函數開頭已經分離好的 thSensorIds
        // (thSensorIds 已經在函數開頭根據設備前綴過濾出來了)
        
        logger.log('📊 [HourlyChartData] TH sensor deviceSNs found:', thSensorIds);
        
        // Only fetch sensor report if we have TH sensors (devices 為必填參數)
        if (thSensorIds.length > 0) {
          logger.log('📊 [HourlyChartData] Fetching sensor data for:', thSensorIds);
          
          // API limit: max 3 devices per custom time range query
          // Split into batches of 3
          const BATCH_SIZE = 3;
          const batches: string[][] = [];
          for (let i = 0; i < thSensorIds.length; i += BATCH_SIZE) {
            batches.push(thSensorIds.slice(i, i + BATCH_SIZE));
          }
          
          // Fetch all batches in parallel
          // ✅ 使用新的 generateReport API 簽名
          const endTime = dayEnd.toISOString();
          const startTime = dayStart.toISOString();
          // 計算 duration（小時數）
          const hoursDiff = Math.ceil((new Date(endTime).getTime() - new Date(startTime).getTime()) / (1000 * 60 * 60));
          const duration = `${hoursDiff}h`;
          
          const batchResults = await Promise.all(
            batches.map(batch => 
              this.generateReport(batch, {
                duration,
                endTime,
                format: 'detailed',  // ✅ 使用 detailed 格式以獲取實際數據
                granularity: 'hour',  // ✅ 強制使用小時級粒度
                utcOffset: '+8:00'
              }).catch(err => {
                logger.warn('⚠️ [HourlyChartData] Batch failed:', err);
                return null;
              })
            )
          );
          
          // Process all batch results
          // ✅ Structure: hourlyAggregatesByDevice[hour][deviceSN] = { temps: [], humidities: [] }
          // This allows us to average per device first, then average across devices
          const hourlyAggregatesByDevice: Record<string, Record<string, { temps: number[]; humidities: number[] }>> = {};
          
          batchResults.forEach((sensorResult, batchIdx) => {
            if (!sensorResult) {
              logger.warn(`⚠️ [HourlyChartData] Batch ${batchIdx} returned null`);
              return;
            }
            
            logger.log(`📊 [HourlyChartData] Processing batch ${batchIdx} result:`, {
              type: sensorResult?.type,
              hasData: !!sensorResult?.data,
              dataKeys: sensorResult?.data ? Object.keys(sensorResult.data) : [],
              fullResult: sensorResult
            });
            
            
            // Parse sensor data - handle multiple possible formats
            // Format 1: { type: 'sensor_data', data: { deviceSN: { sensorGroup: { temperature: [...], humidity: [...] } } } }
            // Format 2: { data: { deviceSN: { temperature: [{value, timestamp}], humidity: [{value, timestamp}] } } }
            // Format 3: Direct data structure
            
            let sensorData: Record<string, Record<string, any>> | null = null;
            
            if (sensorResult?.type === 'sensor_data' && sensorResult?.data) {
              sensorData = sensorResult.data as Record<string, Record<string, any>>;
            } else if (sensorResult?.data) {
              // Try direct data access
              sensorData = sensorResult.data as Record<string, Record<string, any>>;
            } else if (sensorResult && typeof sensorResult === 'object') {
              // Maybe the result itself is the data
              sensorData = sensorResult as any;
            }
            
            if (!sensorData) {
              logger.warn(`⚠️ [HourlyChartData] Batch ${batchIdx} - No valid sensor data structure found`);
              return;
            }
            
            logger.log(`📊 [HourlyChartData] Batch ${batchIdx} - Parsing sensor data:`, {
              deviceCount: Object.keys(sensorData).length,
              deviceSNs: Object.keys(sensorData)
            });
            
            Object.keys(sensorData).forEach(deviceSN => {
              const deviceData = sensorData![deviceSN];
              logger.log(`📊 [HourlyChartData] Processing device ${deviceSN}:`, {
                keys: Object.keys(deviceData),
                deviceData
              });
              
              // ✅ 檢查是否有 chartData 字段（類似於 energy 報告的格式）
              if (deviceData.chartData && Array.isArray(deviceData.chartData)) {
                // 處理 chartData 格式：每個點包含 time, temperature/humidity 或 temp/rh
                deviceData.chartData.forEach((point: any, pointIdx: number) => {
                  const time = point.time || point.timestamp;
                  // ✅ 支持多种字段名：temperature/temp 和 humidity/rh
                  const tempValue = point.temperature !== undefined ? point.temperature : 
                                   (point.temp !== undefined ? point.temp : undefined);
                  const humidityValue = point.humidity !== undefined ? point.humidity : 
                                      (point.rh !== undefined ? point.rh : undefined);
                  
                  if (time && (tempValue !== undefined || humidityValue !== undefined)) {
                    const date = new Date(time);
                    const hour = `${date.getHours().toString().padStart(2, '0')}:00`;
                    
                    if (!hourlyAggregatesByDevice[hour]) {
                      hourlyAggregatesByDevice[hour] = {};
                    }
                    if (!hourlyAggregatesByDevice[hour][deviceSN]) {
                      hourlyAggregatesByDevice[hour][deviceSN] = { temps: [], humidities: [] };
                    }
                    
                    if (tempValue !== undefined && tempValue !== null) {
                      hourlyAggregatesByDevice[hour][deviceSN].temps.push(tempValue);
                    }
                    if (humidityValue !== undefined && humidityValue !== null) {
                      hourlyAggregatesByDevice[hour][deviceSN].humidities.push(humidityValue);
                    }
                  }
                });
                return; // 已處理 chartData，跳過後續的 sensorGroup 處理
              }
              
              // ✅ 排除元數據字段，只處理實際的傳感器數據組
              const metadataKeys = ['deviceType', 'availableMetrics', 'returnedMetrics', 'dataPoints', 'warning', 'chartData'];
              const sensorGroups = Object.keys(deviceData).filter(key => {
                const value = deviceData[key];
                return !metadataKeys.includes(key) && typeof value === 'object' && value !== null && !Array.isArray(value);
              });
              
              sensorGroups.forEach(sensorGroup => {
                const groupData = deviceData[sensorGroup];
                logger.log(`📊 [HourlyChartData] Processing sensor group ${sensorGroup} for ${deviceSN}:`, {
                  hasTemperature: !!groupData?.temperature,
                  hasHumidity: !!groupData?.humidity,
                  hasTimestamps: !!groupData?.timestamps,
                  temperatureType: Array.isArray(groupData?.temperature) ? 'array' : typeof groupData?.temperature,
                  humidityType: Array.isArray(groupData?.humidity) ? 'array' : typeof groupData?.humidity,
                  groupData
                });
                
                // Check if temperature data exists
                if (groupData?.temperature && Array.isArray(groupData.temperature)) {
                  // Format 1: Each item has {value, timestamp}
                  if (groupData.temperature.length > 0 && groupData.temperature[0]?.timestamp !== undefined) {
                    logger.log(`📊 [HourlyChartData] Format 1 detected for ${deviceSN}/${sensorGroup}: ${groupData.temperature.length} temperature points`);
                    groupData.temperature.forEach((item: { value: number; timestamp: number }, idx: number) => {
                      // timestamp is in seconds, convert to milliseconds
                      const date = new Date(item.timestamp * 1000);
                      // ✅ Shift time back by 1 hour to align with start time
                      date.setHours(date.getHours() - 1);
                      const hour = `${date.getHours().toString().padStart(2, '0')}:00`;
                      
                      if (!hourlyAggregatesByDevice[hour]) {
                        hourlyAggregatesByDevice[hour] = {};
                      }
                      if (!hourlyAggregatesByDevice[hour][deviceSN]) {
                        hourlyAggregatesByDevice[hour][deviceSN] = { temps: [], humidities: [] };
                      }
                      
                      hourlyAggregatesByDevice[hour][deviceSN].temps.push(item.value);
                      
                      // Get corresponding humidity
                      const humidityItem = groupData.humidity?.[idx];
                      if (humidityItem?.value !== undefined) {
                        hourlyAggregatesByDevice[hour][deviceSN].humidities.push(humidityItem.value);
                      }
                    });
                  }
                  // Format 2: Separate timestamps array
                  else if (groupData?.timestamps && Array.isArray(groupData.timestamps)) {
                    logger.log(`📊 [HourlyChartData] Format 2 detected for ${deviceSN}/${sensorGroup}: ${groupData.timestamps.length} timestamps`);
                    groupData.timestamps.forEach((timestamp: string | number, idx: number) => {
                      const date = new Date(typeof timestamp === 'number' ? timestamp * 1000 : timestamp);
                      // ✅ Shift time back by 1 hour to align with start time
                      date.setHours(date.getHours() - 1);
                      const hour = `${date.getHours().toString().padStart(2, '0')}:00`;
                      
                      if (!hourlyAggregatesByDevice[hour]) {
                        hourlyAggregatesByDevice[hour] = {};
                      }
                      if (!hourlyAggregatesByDevice[hour][deviceSN]) {
                        hourlyAggregatesByDevice[hour][deviceSN] = { temps: [], humidities: [] };
                      }
                      
                      if (groupData.temperature?.[idx] !== undefined) {
                        hourlyAggregatesByDevice[hour][deviceSN].temps.push(groupData.temperature[idx]);
                      }
                      if (groupData.humidity?.[idx] !== undefined) {
                        hourlyAggregatesByDevice[hour][deviceSN].humidities.push(groupData.humidity[idx]);
                      }
                    });
                  } else {
                    logger.warn(`⚠️ [HourlyChartData] Unknown temperature format for ${deviceSN}/${sensorGroup}`);
                  }
                } else {
                  logger.warn(`⚠️ [HourlyChartData] No temperature array found for ${deviceSN}/${sensorGroup}`);
                }
              });
            });
          });
          
          logger.log('📊 [HourlyChartData] Aggregated data by device:', {
            hours: Object.keys(hourlyAggregatesByDevice),
            hourlyAggregatesByDevice
          });
          
          // ✅ Calculate averages: first average per device per hour, then average across all selected devices
          Object.keys(hourlyAggregatesByDevice).forEach(hour => {
            const devicesInHour = Object.keys(hourlyAggregatesByDevice[hour]);
            const deviceAverages: { temp: number; humidity: number }[] = [];
            
            // Step 1: Calculate average for each device in this hour
            devicesInHour.forEach(deviceSN => {
              const deviceData = hourlyAggregatesByDevice[hour][deviceSN];
              
              // Average temperature for this device in this hour
              const deviceAvgTemp = deviceData.temps.length > 0
                ? deviceData.temps.reduce((a, b) => a + b, 0) / deviceData.temps.length
                : null;
              
              // Average humidity for this device in this hour
              const deviceAvgHumidity = deviceData.humidities.length > 0
                ? deviceData.humidities.reduce((a, b) => a + b, 0) / deviceData.humidities.length
                : null;
              
              if (deviceAvgTemp !== null && deviceAvgHumidity !== null) {
                deviceAverages.push({ temp: deviceAvgTemp, humidity: deviceAvgHumidity });
              }
            });
            
            // Step 2: Average across all selected devices
            if (deviceAverages.length > 0) {
              const avgTemp = deviceAverages.reduce((sum, d) => sum + d.temp, 0) / deviceAverages.length;
              const avgHumidity = deviceAverages.reduce((sum, d) => sum + d.humidity, 0) / deviceAverages.length;
              
              climateData.push({
                hour,
                temp: parseFloat(avgTemp.toFixed(1)),
                humidity: Math.round(avgHumidity)
              });
            }
          });
          
          logger.log('📊 [HourlyChartData] Processed climate data:', climateData);
        } else {
          logger.log('⏭️ [HourlyChartData] Skipping sensor report - no TH sensors or locationId');
        }
      } catch (sensorError) {
        logger.warn('⚠️ [HourlyChartData] Failed to get sensor data:', sensorError);
      }

      // Fill missing climate hours
      const filledClimateData: Array<{hour: string, temp: number, humidity: number}> = [];
      for (let i = 0; i < 24; i++) {
        const hour = `${i.toString().padStart(2, '0')}:00`;
        const existing = climateData.find(c => c.hour === hour);
        if (existing) {
          filledClimateData.push(existing);
        } else {
          filledClimateData.push({ hour, temp: 0, humidity: 0 });
        }
      }

      logger.log(`📊 [HourlyChartData] Final totalEnergyFromApi: ${totalEnergyFromApi} kWh, foundTotalConsumption: ${foundTotalConsumption}`);
      
      return {
        powerData: filledPowerData,
        climateData: filledClimateData,
        // ✅ 只要找到了 totalConsumption 字段（即使值是0），就返回它
        totalEnergy: foundTotalConsumption ? totalEnergyFromApi : undefined
      };
    } catch (error) {
      logger.error('❌ [HourlyChartData] Failed to generate hourly chart data:', error);
      // Return empty data
      const emptyData = [];
      for (let i = 0; i < 24; i++) {
        emptyData.push({
          hour: `${i.toString().padStart(2, '0')}:00`,
          power: 0,
          energy: 0,
          temp: 0,
          humidity: 0
        });
      }
      return {
        powerData: emptyData.map(d => ({ hour: d.hour, power: d.power, energy: d.energy })),
        climateData: emptyData.map(d => ({ hour: d.hour, temp: d.temp, humidity: d.humidity }))
      };
    }
  },

  /**
   * Export sensor data history to CSV
   */
  async exportSensorDataToCSV(params: {
    sn: string;
    startTime: string;
    endTime: string;
    fileName?: string;
    types?: string[];
    getRawData?: boolean;
  }): Promise<any> {
    try {
      const result = await turingRequest('ExportSensorDataHistoryToCSV', {
        sn: params.sn,
        startTime: params.startTime,
        endTime: params.endTime,
        fileName: params.fileName || `sensor_data_${params.sn}_${Date.now()}.csv`,
        types: params.types || ['temperature', 'humidity'],
        getRawData: params.getRawData || false
      });
      logger.log('📊 CSV Export result:', result);
      return result;
    } catch (error) {
      logger.error('Error exporting sensor data to CSV:', error);
      throw error;
    }
  },

  /**
   * Configure Alert via HTTP API
   */

  async configureAlert(config: {
    name: string;
    device: string;
    deviceName?: string;  // 設備友善名稱，推送時會包含
    metric: 'temperature' | 'humidity' | 'power' | 'co2' | 'tvoc' | 'battery' | 'presence' | 'voltage' | 'current' | 'energy' | 'power_state' | 'online' | 'lux' | 'door_state' | 'motion';
    condition: 'above' | 'below' | 'equals' | '>=' | '<=' | 'detected' | 'not_detected' | 'on' | 'off' | 'changed' | 'online' | 'offline' | 'opened' | 'closed';
    threshold: number;
    severity?: 'info' | 'warning' | 'major' | 'critical';
    notificationChannels?: ('websocket' | 'line' | 'toast' | 'system')[];
    groupId?: string;
    id?: string;
    triggerMode?: 'edge' | 'continuous' | 'interval';
    intervalMinutes?: number;
    enabled?: boolean;  // 啟用/暫停警報（預設 true）
  }): Promise<any> {
    try {
      // Check auth mode
      const authMode = sessionStorage.getItem('auth_mode');
      let headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Ultron-Cloud-Appid': 'KLMGeDMy'
      };
      let defaultGroupId = '';

      if (authMode === 'oauth') {
        // ✅ OAuth mode - use Bearer token from sessionStorage
        const accessToken = getOAuthAccessToken();
        if (!accessToken) {
          throw new Error('Missing OAuth token. Please login again.');
        }
        headers['Authorization'] = `Bearer ${accessToken}`;
        logger.log('🔐 Using OAuth for alert configuration');

        // OAuth users need to get groupId from QueryLocations if not provided
        if (!config.groupId) {
          logger.log('📍 Getting groupId from QueryLocations...');
          const locations = await this.queryLocations();
          const locationsData: any = locations.data || locations;
          const locationsArray = Array.isArray(locationsData) 
            ? locationsData 
            : locationsData?.locations || locationsData?.groups || [];
          if (locationsArray.length > 0) {
            const firstLoc = locationsArray[0];
            defaultGroupId = (firstLoc.id || firstLoc.groupId || '') as string;
            if (defaultGroupId) {
              logger.log('📍 Using first group:', defaultGroupId);
            }
          }
        }
      } else {
        // API Key mode
        const credentials = getCredentials();
        const { apiKey, appId, groupId } = credentials;
        if (!apiKey || !appId) {
          throw new Error('Missing API credentials. Please configure in Settings.');
        }
        headers['X-Api-Key'] = apiKey;
        headers['Ultron-Cloud-Appid'] = appId;
        defaultGroupId = groupId || '';
        logger.log('🔐 Using API Key for alert configuration');
      }

      // Build URL - use MCP endpoint for alert configuration
      const url = `${getTuringApiUrl()}/mcp/ConfigureAlert`;

      // MCP ConfigureAlert requires action: create/update/delete
      // Validate ID for update operations - must be non-empty string
      const hasValidId = config.id && typeof config.id === 'string' && config.id.trim() !== '';
      const action = hasValidId ? 'update' : 'create';

      // Convert UI condition values to MCP operators
      // MCP requires: gt, gte, lt, lte, eq, neq
      const conditionMap: Record<string, string> = {
        'above': 'gt',
        'below': 'lt',
        'equals': 'eq',
        '>=': 'gte',
        '<=': 'lte',
        'detected': 'eq',
        'not_detected': 'neq',
        'on': 'eq',  // For power_state: on
        'off': 'eq',  // For power_state: off
        'changed': 'neq',  // For state changes
        'online': 'eq',  // For online status
        'offline': 'eq'  // For offline status
      };
      const mcpCondition = conditionMap[config.condition] || config.condition;
      
      // For state-based metrics, threshold should be 0 or can be omitted
      // But we'll still send it as 0 to maintain API compatibility
      const threshold = (config.metric === 'presence' || config.metric === 'power_state' || config.metric === 'online') 
        ? 0 
        : config.threshold;

      const body: any = {
        action,
        name: config.name,
        device: config.device,
        metric: config.metric,
        condition: mcpCondition,
        threshold: threshold,
        severity: config.severity || 'warning',
        notificationChannels: config.notificationChannels || ['websocket'],
        groupId: config.groupId || defaultGroupId,
        triggerMode: config.triggerMode || 'edge',
        enabled: config.enabled !== false  // 預設啟用
      };

      // Add id for updates - only if ID is valid
      if (hasValidId) {
        body.id = config.id;
      }

      // Add intervalMinutes if triggerMode is 'interval'
      if (config.triggerMode === 'interval' && config.intervalMinutes) {
        body.intervalMinutes = config.intervalMinutes;
      }

      logger.secure('🔔 Configuring alert:', body); // 使用 secure 方法脱敏

      // MCP endpoints always use POST
      logger.log(`📤 Using MCP ConfigureAlert, action: ${action}, URL: ${url}`);

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('❌ Alert configuration failed:', response.status, errorText);
        throw new Error(`Failed to configure alert: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      logger.log('✅ Alert configured successfully:', result);
      return result;
    } catch (error) {
      logger.error('Error configuring alert:', error);
      throw error;
    }
  },

  /**
   * Configure global battery alert (alertType: "battery")
   * This alert monitors ALL devices with battery capability automatically
   * 
   * NOTE: Notification channels are now managed via Notification Center (QueryNotifications set_preferences)
   * The notificationChannels parameter is deprecated and should not be used.
   * 
   * @param config - Alert configuration
   * @returns API response
   */
  async configureGlobalBatteryAlert(config: {
    action: 'create' | 'update' | 'delete';
    id?: string;
    name: string;
    threshold?: number;
    enabled?: boolean;  // Enable/disable the alert (default: true)
    email?: string;  // Custom email address (optional, uses account email if not provided)
    schedule?: {
      time: string;  // HH:MM format, e.g., '09:00'
      timezone: string;  // IANA timezone, e.g., 'Asia/Taipei'
    };
  }): Promise<any> {
    // Build request body for global battery alert
    // Key: alertType: "battery" routes to handleConfigureBatteryAlert on server
    // Note: notificationChannels is deprecated - use Notification Center settings instead
    const params: Record<string, unknown> = {
      alertType: 'battery',  // MCP API uses 'battery' to indicate global battery alert
      action: config.action,
      name: config.name,
      threshold: config.threshold ?? 20,  // Default 20%
      enabled: config.enabled !== false,  // Default to true
      schedule: config.schedule || {
        time: '09:00',
        timezone: 'Asia/Taipei'
      }
    };

    // Add id for update/delete
    if (config.id) {
      params.id = config.id;
    }

    // Add custom email if provided (for battery alert specific email)
    if (config.email) {
      params.email = config.email;
    }

    logger.log('🔋 Configuring global battery alert via turingRequest:', params);

    // Use turingRequest like other MCP calls
    const result = await turingRequest('ConfigureAlert', params);
    
    logger.log('✅ Global battery alert configured:', result);
    return result;
  },

  /**
   * Get power meters with real-time data
   */
  async getPowerMeters(): Promise<{ powerMeters: PowerMeter[], realTimeData: Record<string, RealTimeData> }> {
    // Query ALL devices first, then filter for power meters
    const devices = await this.queryDevices(undefined, {
      status: 'all'
      // Remove type filter to get ALL devices
    });
    logger.log('🔍 getPowerMeters - All devices from queryDevices:', devices.length);

    // Use Turing API if it returns devices
    if (devices.length > 0) {
      logger.log('✅ Using Ultron Turing devices:', devices.length);
      logger.log('🔍 Sample devices:', devices.slice(0, 3).map(d => ({ sn: d.sn, name: d.name, type: d.type, traits: d.traits })));

      // Transform devices to PowerMeter format
      // Filter for devices that actually have power monitoring capabilities
      const powerMeters: PowerMeter[] = devices
        .filter(device => {
          // Include devices with Sensor trait AND SWITCH type
          // OR UT3M series (power meters)
          const hasSensorTrait = device.traits?.includes('Sensor');
          const isSwitchType = device.type === 'SWITCH';
          const isPowerMeter = device.sn?.startsWith('UT3M');

          // Power meters are: UT3M series OR (SWITCH type with Sensor trait)
          return isPowerMeter || (isSwitchType && hasSensorTrait);
        })
        .map((device: any) => {
          // Use current time for initial lastUpdate
          const currentTime = new Date().toISOString();
          return {
            id: device.sn,
            deviceId: device.sn,
            deviceName: device.name || device.sn,
            // ✅ Use device's own group info instead of credentials.groupId
            // This works for both OAuth and API Key users
            groupId: device.group || device.groupName || device.groupId || '',
            status: device.online ? 'online' : 'offline',
            // Use current time initially
            lastUpdate: currentTime,
            location: device.location || '',
            deviceType: 'power_meter',  // These are all power monitoring devices
            online: device.online
          };
        });
      
      // Get real-time status for power meters only
      // MUST pass detailed: true to get real-time power consumption (watt)
      const deviceIds = powerMeters.map(d => d.deviceId);
      const statusResult = await this.queryStatus(deviceIds, { detailed: true });
      logger.log('📊 Status result in getPowerMeters:', statusResult);
      logger.log('📊 Getting status for devices:', deviceIds);

      // Build real-time data map
      const realTimeData: Record<string, RealTimeData> = {};

      // Handle the status result structure
      if (statusResult?.data && Array.isArray(statusResult.data)) {
        logger.log('📊 Status data array length:', statusResult.data.length);
        logger.log('📊 API returns:', statusResult);

        // Always use current time for the timestamp since we just fetched the data
        // The API's timestamp might be stale or in a different timezone
        const apiTimestamp = new Date().toISOString();

        logger.log('📊 Status result timestamp from API:', statusResult.timestamp);
        logger.log('📊 Using current time as timestamp:', apiTimestamp);

        for (const statusItem of statusResult.data) {
          logger.log('📊 Status item:', statusItem);
          const deviceSn = statusItem.device || statusItem.deviceId || statusItem.sn;

          if (deviceSn && deviceSn.startsWith('UT3M')) {
            // Only use real data from API
            // If API doesn't provide power consumption data, show 0
            const powerData = statusItem.status || statusItem;

            realTimeData[deviceSn] = {
              deviceId: deviceSn,
              power: parseFloat(powerData.power) || parseFloat(powerData.activePower) || parseFloat(powerData.current_power) || 0,
              voltage: parseFloat(powerData.voltage) || parseFloat(powerData.lineVoltage) || parseFloat(powerData.current_voltage) || 0,
              current: (parseFloat(powerData.current) || parseFloat(powerData.lineCurrent) || parseFloat(powerData.current_ampere) || 0) / 1000,
              energy: parseFloat(powerData.energy) || parseFloat(powerData.totalEnergy) || parseFloat(powerData.total_energy) || 0,
              powerFactor: parseFloat(powerData.powerFactor) || parseFloat(powerData.pf) || parseFloat(powerData.power_factor) || 0,
              frequency: parseFloat(powerData.frequency) || parseFloat(powerData.freq) || 0,
              // Use the API's global timestamp since individual devices don't have timestamps
              timestamp: apiTimestamp
            };

            logger.log(`📊 Real data for ${deviceSn}:`, realTimeData[deviceSn]);

            // Update the PowerMeter's lastUpdate with the API timestamp
            const meterIndex = powerMeters.findIndex(m => m.deviceId === deviceSn);
            if (meterIndex >= 0) {
              powerMeters[meterIndex].lastUpdate = apiTimestamp;
            }
          }
        }
      } else if (statusResult?.devices && typeof statusResult.devices === 'object') {
        // NEW format: Handle { devices: { "UT3M15-xxx": { online, stateData } } }
        logger.log('📊 Processing new devices object format');
        for (const meter of powerMeters) {
          let status = statusResult.devices[meter.deviceId];
          // Merge stateData into status for easier access
          if (status?.stateData) {
            status = { ...status, ...status.stateData };
          }
          if (status) {
            realTimeData[meter.deviceId] = {
              deviceId: meter.deviceId,
              power: parseFloat(status.power) || parseFloat(status.activePower) || parseFloat(status.watt) || 0,
              voltage: parseFloat(status.voltage) || parseFloat(status.lineVoltage) || 0,
              current: (parseFloat(status.current) || parseFloat(status.lineCurrent) || 0) / 1000,
              energy: parseFloat(status.energy) || parseFloat(status.totalEnergy) || 0,
              powerFactor: parseFloat(status.powerFactor) || parseFloat(status.pf) || 0,
              frequency: parseFloat(status.frequency) || parseFloat(status.freq) || 50,
              timestamp: status.timestamp || status.lastUpdate || status.updatedAt || new Date().toISOString()
            };
            logger.log(`📊 Power data for ${meter.deviceId}:`, realTimeData[meter.deviceId]);

            const meterIndex = powerMeters.findIndex(m => m.deviceId === meter.deviceId);
            if (meterIndex >= 0 && realTimeData[meter.deviceId].timestamp) {
              powerMeters[meterIndex].lastUpdate = realTimeData[meter.deviceId].timestamp;
            }
          }
        }
      } else if (statusResult && typeof statusResult === 'object' && !statusResult.devices && !statusResult.data) {
        // OLD format: Handle direct device-indexed object { "UT3M15-xxx": { power, ... } }
        logger.log('📊 Processing legacy direct object format');
        for (const meter of powerMeters) {
          const status = statusResult[meter.deviceId];
          if (status) {
            realTimeData[meter.deviceId] = {
              deviceId: meter.deviceId,
              power: parseFloat(status.power) || parseFloat(status.activePower) || 0,
              voltage: parseFloat(status.voltage) || parseFloat(status.lineVoltage) || 0,
              current: (parseFloat(status.current) || parseFloat(status.lineCurrent) || 0) / 1000,
              energy: parseFloat(status.energy) || parseFloat(status.totalEnergy) || 0,
              powerFactor: parseFloat(status.powerFactor) || parseFloat(status.pf) || 0,
              frequency: parseFloat(status.frequency) || parseFloat(status.freq) || 50,
              // Use the timestamp from the status data if available
              timestamp: status.timestamp || status.lastUpdate || status.updatedAt || new Date().toISOString()
            };
            logger.log(`📊 Power data for ${meter.deviceId}:`, realTimeData[meter.deviceId]);

            // Update the PowerMeter's lastUpdate with the timestamp from status data
            const meterIndex = powerMeters.findIndex(m => m.deviceId === meter.deviceId);
            if (meterIndex >= 0 && realTimeData[meter.deviceId].timestamp) {
              powerMeters[meterIndex].lastUpdate = realTimeData[meter.deviceId].timestamp;
            }
          }
        }
      }
      
      return { powerMeters, realTimeData };
    }
    
    // No devices found from Turing API
    logger.warn('⚠️ No devices found from Turing API');
    return { powerMeters: [], realTimeData: {} };
  },

  /**
   * Get climate devices
   */
  async getClimateDevices(): Promise<ClimateDevice[]> {
    // Query all devices
    const devices = await this.queryDevices();
    
    // Filter for climate sensors (UTU307, UTU308, UTU31E, UTU31G)
    const climateDevices = devices.filter(d => 
      d.sn?.startsWith('UTU307') || 
      d.sn?.startsWith('UTU308') || 
      d.sn?.startsWith('UTU31E') || 
      d.sn?.startsWith('UTU31G')
    );
    
    logger.log('🌡️ Found climate devices:', climateDevices.length);
    logger.log('🌡️ Climate device IDs:', climateDevices.map(d => d.sn));
    
    // Skip the report approach since it returns usage statistics, not sensor data
    // The updated API should return temperature/humidity directly in QueryStatus
    
    // Check if temperature/humidity data is already in the device object from queryDevices
    if (climateDevices.length > 0) {
      const firstDevice: any = climateDevices[0];
      logger.log('🌡️ First climate device from queryDevices:', firstDevice);
      
      // Check if the device already has temperature/humidity data
      if (firstDevice.temperature !== undefined || firstDevice.humidity !== undefined ||
          firstDevice.attributes?.temperature !== undefined || firstDevice.attributes?.humidity !== undefined) {
        logger.log('🌡️ Device already has sensor data in queryDevices response!');
        
        // Return the devices with data already included
        return climateDevices.map((device: any) => ({
          deviceId: device.sn,
          deviceName: device.name || device.sn,
          deviceType: 'sensor',
          online: device.online,
          status: device.online ? 'online' : 'offline',
          temperature: device.temperature || device.attributes?.temperature || device.attributes?.moko_temperature || 0,
          humidity: device.humidity || device.attributes?.humidity || device.attributes?.moko_humidity || 0,
          co2: device.co2 || device.attributes?.co2,
          pm25: device.pm25 || device.attributes?.pm25,
          location: device.location || '',
          lastUpdate: new Date().toISOString(),
          trend: {
            temperature: 'stable',
            humidity: 'stable'
          }
        }));
      }
    }
    
    // Try to get ALL device statuses at once with detailed data
logger.log('🌡️ Attempting to get ALL device statuses with detailed data...');
    try {
      // Request status data for all devices
      const allStatusData = await this.queryStatus([], { detailed: true }); // Empty array = get all devices, detailed: true for sensor data
logger.log('🌡️ All status data response:', JSON.stringify(allStatusData).substring(0, 1000));
      
      // Build a map of all device statuses
      // Handle both new format (devices object) and old format (data array)
      const statusMap: Record<string, any> = {};
      if (allStatusData?.devices && typeof allStatusData.devices === 'object') {
        // NEW format: { devices: { "UTU308-xxx": { online, stateData } } }
        Object.entries(allStatusData.devices).forEach(([deviceId, status]: [string, any]) => {
          // Merge stateData into status for easier access
          if (status?.stateData) {
            statusMap[deviceId] = { ...status, ...status.stateData };
          } else {
            statusMap[deviceId] = status;
          }
        });
        logger.log('🌡️ Using new devices object format');
      } else if (allStatusData?.data && Array.isArray(allStatusData.data)) {
        // OLD format: { data: [{ device, ... }] }
        allStatusData.data.forEach((item: any) => {
          const deviceId = item.device || item.deviceId || item.sn;
          if (deviceId) {
            statusMap[deviceId] = item;
          }
        });
        logger.log('🌡️ Using legacy data array format');
      }

logger.log('🌡️ Status map keys:', Object.keys(statusMap));
      
      // Map climate devices with their status data
      return climateDevices.map(device => {
        const status = statusMap[device.sn] || {};
        const attrs = status.attributes || {};
        
        let temperature = 0;
        let humidity = 0;
        
        // Check for SENSOR attributes first (UTU308 format)
        if (attrs.SENSOR) {
          temperature = attrs.SENSOR.temperatureAmbientCelsius || 0;
          humidity = attrs.SENSOR.humidityAmbientPercent || 0;
        } 
        // Then check top level (simplified format from QueryStatus)
        else if (status.temperature !== undefined || status.humidity !== undefined) {
          temperature = status.temperature || 0;
          humidity = status.humidity || 0;
        }
        // Fallback to other possible attribute names
        else {
          temperature = status.temp || status.currentTemp || 
                       attrs.moko_temperature || attrs.mokoTemperature || 
                       attrs.temperature || attrs.temp || 0;
          humidity = status.humi || status.currentHumidity || 
                    attrs.moko_humidity || attrs.mokoHumidity || 
                    attrs.humidity || attrs.humi || 0;
        }
        
        // Log what we found for debugging
        if (device.sn.startsWith('UTU')) {
logger.log(`🌡️ Checking ${device.sn} status object:`, JSON.stringify(status));
        }
        
logger.log(`🌡️ ${device.sn} - Temp: ${temperature}°C, Humidity: ${humidity}%`);
        
        return {
          deviceId: device.sn,
          deviceName: device.name || device.sn,
          deviceType: 'sensor',
          online: status.online !== undefined ? status.online : device.online,
          status: (status.online !== undefined ? status.online : device.online) ? 'online' : 'offline',
          temperature,
          humidity,
          co2: attrs.co2 || status.co2,
          pm25: attrs.pm25 || status.pm25,
          location: device.location || '',
          lastUpdate: new Date().toISOString(),
          trend: {
            temperature: 'stable',
            humidity: 'stable'
          }
        };
      });
    } catch (error) {
logger.error('Failed to get all device statuses:', error);
    }
    
    // Fallback: Get status for each device individually (old approach)
    const climateDevicesWithData: ClimateDevice[] = [];
    
    for (const device of climateDevices) {
      try {
        // Query status for single device with detailed data
        const statusData = await this.queryStatus([device.sn], { detailed: true });
logger.log(`🌡️ Individual detailed status for ${device.sn}:`, JSON.stringify(statusData).substring(0, 500));
        
        // Parse the status data
        let temperature = 0;
        let humidity = 0;
        
        // Handle different response formats
        let deviceStatus = null;
        
        // Check if response has data array
        if (statusData && statusData.data && Array.isArray(statusData.data)) {
          // Find the device in the data array
          deviceStatus = statusData.data.find((item: any) => 
            item.device === device.sn || 
            item.deviceId === device.sn || 
            item.sn === device.sn
          );
        } else if (statusData && statusData[device.sn]) {
          // Direct mapping format
          deviceStatus = statusData[device.sn];
        }
        
        if (deviceStatus) {
          
          // Check for attributes object which contains sensor data
          if (deviceStatus.attributes) {
            const attrs = deviceStatus.attributes;
            
            // Look for temperature in various possible locations
            // Check for moko sensor data structure first
            if (attrs.moko_temperature !== undefined) {
              temperature = attrs.moko_temperature;
            } else if (attrs.mokoTemperature !== undefined) {
              temperature = attrs.mokoTemperature;
            } else if (attrs['Ultron Device'] && attrs['Ultron Device'].temperature !== undefined) {
              temperature = attrs['Ultron Device'].temperature;
            } else if (attrs.temperature !== undefined) {
              temperature = attrs.temperature;
            } else if (attrs.temp !== undefined) {
              temperature = attrs.temp;
            } else if (attrs.Temperature !== undefined) {
              temperature = attrs.Temperature;
            }
            
            // Look for humidity in various possible locations
            // Check for moko sensor data structure first
            if (attrs.moko_humidity !== undefined) {
              humidity = attrs.moko_humidity;
            } else if (attrs.mokoHumidity !== undefined) {
              humidity = attrs.mokoHumidity;
            } else if (attrs['Ultron Device'] && attrs['Ultron Device'].humidity !== undefined) {
              humidity = attrs['Ultron Device'].humidity;
            } else if (attrs.humidity !== undefined) {
              humidity = attrs.humidity;
            } else if (attrs.humi !== undefined) {
              humidity = attrs.humi;
            } else if (attrs.Humidity !== undefined) {
              humidity = attrs.Humidity;
            }
            
            // Check in nested sensor data structures
            for (const key in attrs) {
              if (typeof attrs[key] === 'object' && attrs[key] !== null) {
                if (attrs[key].temperature !== undefined && temperature === 0) {
                  temperature = attrs[key].temperature;
                }
                if (attrs[key].humidity !== undefined && humidity === 0) {
                  humidity = attrs[key].humidity;
                }
                if (attrs[key].temp !== undefined && temperature === 0) {
                  temperature = attrs[key].temp;
                }
                if (attrs[key].humi !== undefined && humidity === 0) {
                  humidity = attrs[key].humi;
                }
              }
            }
          }
          
          // Also check top-level fields
          if (temperature === 0 && deviceStatus.temperature !== undefined) {
            temperature = deviceStatus.temperature;
          }
          if (humidity === 0 && deviceStatus.humidity !== undefined) {
            humidity = deviceStatus.humidity;
          }
        } else {
logger.log(`🌡️ No device found in response for ${device.sn}`);
        }
        
logger.log(`🌡️ ${device.sn} - Temp: ${temperature}°C, Humidity: ${humidity}%`);
        
        climateDevicesWithData.push({
          deviceId: device.sn,
          deviceName: device.name || device.sn,
          deviceType: 'sensor',
          online: device.online,
          status: device.online ? 'online' : 'offline',
          temperature,
          humidity,
          co2: undefined,
          pm25: undefined,
          location: device.location || '',
          lastUpdate: new Date().toISOString(),
          trend: {
            temperature: 'stable',
            humidity: 'stable'
          }
        });
      } catch (error) {
logger.error(`Failed to get status for ${device.sn}:`, error);
        // Add device with no data if status query fails
        climateDevicesWithData.push({
          deviceId: device.sn,
          deviceName: device.name || device.sn,
          deviceType: 'sensor',
          online: device.online,
          status: device.online ? 'online' : 'offline',
          temperature: 0,
          humidity: 0,
          co2: undefined,
          pm25: undefined,
          location: device.location || '',
          lastUpdate: new Date().toISOString(),
          trend: {
            temperature: 'stable',
            humidity: 'stable'
          }
        });
      }
    }
    
    return climateDevicesWithData;
  },

  /**
   * Generate alerts based on device status
   */
  async generateAlerts(): Promise<SystemAlert[]> {
    const alerts: SystemAlert[] = [];
    
    // Get all devices and their status
    const devices = await this.queryDevices();
    const deviceIds = devices.map(d => d.sn);
    const statusData = await this.queryStatus(deviceIds, { detailed: true });

    // Check each device for alert conditions
    devices.forEach(device => {
      const status = statusData[device.sn];
      
      // Offline alert
      if (!device.online) {
        alerts.push({
          id: `offline-${device.sn}`,
          type: 'offline',
          severity: 'high',
          deviceId: device.sn,
          deviceName: device.name || device.sn,
          message: `Device ${device.name || device.sn} is offline`,
          timestamp: new Date().toISOString(),
          acknowledged: false
        });
      }
      
      // Power alerts for UT3M devices
      if (device.sn?.startsWith('UT3M') && status) {
        // High power alert
        if (status.power > 5000) {
          alerts.push({
            id: `high-power-${device.sn}`,
            type: 'high_power',
            severity: status.power > 8000 ? 'critical' : 'medium',
            deviceId: device.sn,
            deviceName: device.name || device.sn,
            message: `High power consumption: ${status.power.toFixed(0)}W`,
            timestamp: new Date().toISOString(),
            acknowledged: false
          });
        }
        
        // Low power alert (potential issue)
        if (device.online && status.power < 10) {
          alerts.push({
            id: `low-power-${device.sn}`,
            type: 'low_power',
            severity: 'low',
            deviceId: device.sn,
            deviceName: device.name || device.sn,
            message: `Abnormally low power: ${status.power.toFixed(1)}W`,
            timestamp: new Date().toISOString(),
            acknowledged: false
          });
        }
        
        // Voltage alert
        if (status.voltage > 0 && (status.voltage < 200 || status.voltage > 240)) {
          alerts.push({
            id: `voltage-${device.sn}`,
            type: 'voltage_alert',
            severity: 'high',
            deviceId: device.sn,
            deviceName: device.name || device.sn,
            message: `Abnormal voltage: ${status.voltage.toFixed(0)}V`,
            timestamp: new Date().toISOString(),
            acknowledged: false
          });
        }
      }
      
      // Climate alerts for UTU307, UTU308, UTU31E, UTU31G
      const isClimateDevice = 
        device.sn?.startsWith('UTU307') || 
        device.sn?.startsWith('UTU308') || 
        device.sn?.startsWith('UTU31E') || 
        device.sn?.startsWith('UTU31G');
      
      if (isClimateDevice && status) {
        // Temperature alerts
        if (status.temperature > 30) {
          alerts.push({
            id: `high-temp-${device.sn}`,
            type: 'high_power' as const, // Using high_power as a general warning type
            severity: status.temperature > 35 ? 'high' : 'medium',
            deviceId: device.sn,
            deviceName: device.name || device.sn,
            message: `High temperature: ${status.temperature.toFixed(1)}°C`,
            timestamp: new Date().toISOString(),
            acknowledged: false
          });
        }
        
        // Humidity alerts
        if (status.humidity > 70 || status.humidity < 30) {
          alerts.push({
            id: `humidity-${device.sn}`,
            type: 'low_power' as const, // Using low_power as a general warning type
            severity: 'medium',
            deviceId: device.sn,
            deviceName: device.name || device.sn,
            message: `Abnormal humidity: ${status.humidity.toFixed(0)}%`,
            timestamp: new Date().toISOString(),
            acknowledged: false
          });
        }
      }
    });
    
    return alerts;
  },

  /**
   * Get energy analytics
   */
  async getEnergyAnalytics(period: string = 'day'): Promise<any> {
    // Get devices first (generateReport 現在需要 deviceSNs 參數)
    const devices = await this.queryDevices();
    const ut3mDevices = devices.filter(d => d.sn?.startsWith('UT3M'));
    const deviceIds = ut3mDevices.map(d => d.sn).filter((sn): sn is string => !!sn);
    
    // 根據 period 計算 duration 和 granularity
    let duration: string;
    let granularity: 'minute' | 'hour' | 'day' | 'auto' = 'auto';
    
    switch (period) {
      case 'hour':
        duration = '24h';
        granularity = 'hour';
        break;
      case 'day':
        duration = '1d';
        granularity = 'day';
        break;
      case 'week':
        duration = '7d';
        granularity = 'day';
        break;
      case 'month':
        duration = '30d';
        granularity = 'day';
        break;
      default:
        duration = '1d';
        granularity = 'day';
    }
    
    // Generate energy report with device IDs
    const report = deviceIds.length > 0 
      ? await this.generateReport(deviceIds, { duration, granularity, utcOffset: '+8:00' })
      : { success: false, error: 'No UT3M devices found' };
    const statusData = await this.queryStatus(ut3mDevices.map(d => d.sn), { detailed: true });

    // Calculate totals
    let totalPower = 0;
    let totalEnergy = 0;
    
    ut3mDevices.forEach(device => {
      const status = statusData[device.sn];
      if (status) {
        totalPower += status.power || 0;
        totalEnergy += status.energy || 0;
      }
    });
    
    return {
      totalPower,
      totalEnergy,
      avgPower: totalPower / ut3mDevices.length,
      peakPower: (report as { peakPower?: number }).peakPower || totalPower * 1.2,
      report
    };
  },

  /**
   * Configure alert automation
   */
  async setupAlertAutomation(): Promise<void> {
    // Configure offline device alert
    await this.configureAutomation({
      name: 'Device Offline Alert',
      trigger: {
        type: 'device',
        config: {
          event: 'offline',
          devices: ['all']
        }
      },
      actions: [{
        type: 'alert',
        config: {
          severity: 'high',
          message: 'Device went offline'
        }
      }]
    });

    // Configure high power alert
    await this.configureAutomation({
      name: 'High Power Consumption Alert',
      trigger: {
        type: 'sensor',
        config: {
          metric: 'power',
          threshold: 5000,
          condition: 'above'
        }
      },
      actions: [{
        type: 'alert',
        config: {
          severity: 'medium',
          message: 'Power consumption exceeded threshold'
        }
      }]
    });

    // Configure temperature alert
    await this.configureAutomation({
      name: 'High Temperature Alert',
      trigger: {
        type: 'sensor',
        config: {
          metric: 'temperature',
          threshold: 30,
          condition: 'above'
        }
      },
      actions: [{
        type: 'alert',
        config: {
          severity: 'medium',
          message: 'Temperature exceeded threshold'
        }
      }]
    });

logger.log('✅ Alert automation configured');
  },

  /**
   * OAuth Authentication Methods
   */

  /**
   * Initiate OAuth login flow
   */
  async initiateOAuthLogin(params: {
    redirect_uri?: string;
    scope?: string;
    state?: string;
    tenant_id?: string;
  } = {}): Promise<{ authUrl: string; state: string }> {
    try {
      const response = await turingRequest('InitiateOAuthLogin', {
        redirect_uri: params.redirect_uri,
        scope: params.scope || 'openid profile email',
        state: params.state,
        tenant_id: params.tenant_id
      });

      return {
        authUrl: response.auth_url,
        state: response.state || params.state
      };
    } catch (error) {
logger.error('OAuth initiation error:', error);
      throw error;
    }
  },

  /**
   * Check authentication session status
   */
  async checkAuthSession(sessionId?: string): Promise<{
    authenticated: boolean;
    authType?: 'oauth' | 'apikey';
    tenantId?: string;
    sessionId?: string;
    user?: any;
  }> {
    try {
      // Get credentials
      const storedCredentials = localStorage.getItem('ultron-energy-credentials');
      if (!storedCredentials) {
        return { authenticated: false };
      }

      const credentials = JSON.parse(storedCredentials);
      const groupId = String(credentials.groupId || 'rTtAr4wDmmkQBKTYbLBiNB').trim();

logger.log('🔐 Checking API Key authentication...');

      // Use CheckAuthSession MCP endpoint which is designed for authentication
      const result = await turingRequest('CheckAuthSession', {
        session_id: sessionId
      });

logger.log('🔐 Auth check result:', result);

      // If we got a successful response, auth is valid
      if (result && result.authenticated) {
logger.log('🔐 API Key validated successfully');
        return {
          authenticated: true,
          authType: result.authType || 'apikey',
          tenantId: result.tenantId || credentials.appId,
          sessionId: result.sessionId || sessionId,
          user: result.user || { appId: credentials.appId, groupId }
        };
      }

      return { authenticated: false };
    } catch (error) {
logger.error('Session check error:', error);
      return { authenticated: false };
    }
  },

  /**
   * Refresh OAuth token
   */
  async refreshOAuthToken(refreshToken: string, tenantId?: string): Promise<{
    accessToken: string;
    refreshToken?: string;
    expiresIn: number;
  }> {
    try {
      const response = await turingRequest('RefreshOAuthToken', {
        refresh_token: refreshToken,
        tenant_id: tenantId
      });

      return {
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
        expiresIn: response.expires_in || 3600
      };
    } catch (error) {
logger.error('Token refresh error:', error);
      throw error;
    }
  },

  /**
   * Switch authentication method between OAuth and API Key
   */
  async switchAuthMethod(params: {
    auth_method: 'oauth' | 'apikey';
    oauth_token?: string;
    api_key?: string;
    app_id?: string;
  }): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await turingRequest('SwitchAuthMethod', params);

      return {
        success: response.success || false,
        message: response.message
      };
    } catch (error) {
logger.error('Auth switch error:', error);
      return { success: false, message: 'Failed to switch authentication method' };
    }
  },


  /**
   * Remove configuration (alert, scene, automation, schedule)
   * Note: For alerts, now using ConfigureAlert with action: 'delete' (QueryAlerts API was removed)
   */
  async removeConfiguration(type: 'alert' | 'scene' | 'automation' | 'schedule', id: string, locationId?: string): Promise<any> {
    try {
      if (type === 'alert') {
        // Use ConfigureAlert with action: 'delete' for alerts
        logger.log(`🗑️ Deleting alert via ConfigureAlert:`, { id });
        const result = await turingRequest('ConfigureAlert', {
          action: 'delete',
          id: id,
          name: '' // Required field, empty for delete
        });
        logger.log(`✅ Alert deleted successfully:`, result);
        return result;
      }

      // For other types (scene, automation, schedule), use MCP endpoint
      const authMode = sessionStorage.getItem('auth_mode');
      let headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Ultron-Cloud-Appid': 'KLMGeDMy'
      };

      if (authMode === 'oauth') {
        // ✅ OAuth mode - use Bearer token from sessionStorage
        const accessToken = getOAuthAccessToken();
        if (!accessToken) {
          throw new Error('Missing OAuth token. Please login again.');
        }
        headers['Authorization'] = `Bearer ${accessToken}`;
      } else {
        // API Key mode
        const credentials = getCredentials();
        const apiKey = String(credentials.apiKey || '').trim().replace(/[^a-zA-Z0-9]/g, '');
        const appId = String(credentials.appId || '').trim();
        headers['X-Api-Key'] = apiKey;
        headers['Ultron-Cloud-Appid'] = appId;
      }

      // MCP endpoint for other types (scene, automation, schedule)
      const url = `${getTuringApiUrl()}/mcp/RemoveConfiguration`;
      const body: Record<string, string> = { type, id };
      if (locationId) {
        body.locationId = locationId;
      }
      logger.log(`🗑️ Deleting ${type} via MCP RemoveConfiguration:`, { url, body });

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        credentials: 'include'
      });

      const data = await response.json();

      // 404 is OK - item was already deleted or doesn't exist
      if (response.status === 404) {
        logger.log(`⚠️ ${type} already deleted or not found:`, data);
        return { success: true, message: `${type} already removed`, wasAlreadyDeleted: true };
      }

      if (!response.ok || data.error || data.success === false) {
        logger.error(`❌ Failed to delete ${type}:`, data);
        const errorMsg = typeof data.error === 'object'
          ? JSON.stringify(data.error)
          : (data.error || data.message || `Failed to delete ${type}`);
        throw new Error(errorMsg);
      }

      logger.log(`✅ ${type} deleted successfully:`, data);
      return data;
    } catch (error) {
      logger.error('Remove configuration error:', error);
      throw error;
    }
  },

  /**
   * Get user info (OAuth only)
   * Note: This endpoint may not be available on all Turing Server versions
   */
  async getUserInfo(): Promise<any> {
    try {
      const result = await turingRequest('GetUserInfo', {});
      logger.log('👤 GetUserInfo result:', result);
      return result;
    } catch (error: any) {
      // 404 means endpoint doesn't exist - this is OK, not an error
      if (error?.message?.includes('404') || error?.response?.status === 404) {
        logger.log('⚠️ GetUserInfo endpoint not available (404) - this is OK');
        return { userInfo: null, available: false };
      }
      // For other errors (like 401), log but don't throw - let caller handle gracefully
      logger.warn('⚠️ GetUserInfo error (non-critical):', error);
      return { userInfo: null, available: false, error: error?.message };
    }
  },

  /**
   * Configure HVAC AI Group
   */
  async configureHVACAI(config: {
    hvacGroupId: string;  // ✅ Use hvacGroupId
    name: string;
    locationId: string;
    devices: {
      hvac: string[];
      thSensor?: string[]; // Optional - HVAC devices have built-in TH sensors
      presenceSensor?: string[];
      co2Sensor?: string[];
    };
    aiSettings?: {
      enabled: boolean;
      learningMode: boolean;
      comfortSettings: {
        targetTemp: number;
        tempRange: number;
        humidityRange: [number, number];
        co2Limit?: number;
        useThermalComfort?: boolean;
      };
      controlBehavior?: {
        autoTurnOn: boolean;
      };
      schedule?: {
        enabled: boolean;
        daysOfWeek: number[];
        timeRanges: Array<{ start: string; end: string }>;
        timezone: string;
      };
    };
  }): Promise<any> {
    try {
logger.log('🤖 Configuring HVAC AI:', config);
      const result = await turingRequest('ConfigureHVACAI', config);
logger.log('✅ HVAC AI configured:', result);
      return result;
    } catch (error) {
logger.error('Configure HVAC AI error:', error);
      throw error;
    }
  },

  /**
   * Get HVAC AI Group Status
   * @param groupId - Optional group ID. If not provided, returns all groups
   */
  async getHVACAIStatus(groupId?: string): Promise<any> {
    try {
logger.log('📊 Getting HVAC AI status for group:', groupId || 'all groups');
      const params = groupId ? { hvacGroupId: groupId } : {};
      const result = await turingRequest('QueryHVACAI', params);
logger.log('✅ HVAC AI status:', result);
      return result;
    } catch (error) {
logger.error('Get HVAC AI status error:', error);
      throw error;
    }
  },

  /**
   * Configure Lighting AI Group
   */
  async configureLightingAI(config: {
    aiGroupId: string;  // ✅ Use aiGroupId to match API specification
    name: string;
    locationId: string;
    devices: {
      lights: string[];
      luxSensor: string[];
      presenceSensor?: string[];
    };
    aiSettings: {
      enabled: boolean;
      controlBehavior?: {
        autoTurnOn: boolean;
      };
      lightingSettings: {
        targetLux: number;
        luxTolerance: number;
        minBrightness: number;
        maxBrightness: number;
        minColorTemp?: number;
        maxColorTemp?: number;
        adaptiveColorTemp?: boolean;
        fixedColorTemp?: number;  // ✅ Added fixedColorTemp field
      };
      presenceControl: {
        autoOff: boolean;
        offDelay: number;
      };
      schedule?: {
        enabled: boolean;
        daysOfWeek: number[];
        timeRanges: Array<{ start: string; end: string }>;
        timezone?: string;
      };
    };
  }): Promise<any> {
    try {
logger.log('💡 Configuring Lighting AI:', config);
      const result = await turingRequest('ConfigureLightingAI', config);
logger.log('✅ Lighting AI configured:', result);
      return result;
    } catch (error) {
logger.error('Configure Lighting AI error:', error);
      throw error;
    }
  },

  /**
   * Get Lighting AI Group Status
   * @param groupId - Optional group ID. If not provided, returns all groups
   */
  async getLightingAIStatus(groupId?: string): Promise<any> {
    try {
logger.log('📊 Getting Lighting AI status for group:', groupId || 'all groups');
      const params = groupId ? { aiGroupId: groupId } : {};
      const result = await turingRequest('QueryLightingAI', params);
logger.log('✅ Lighting AI status:', result);
      return result;
    } catch (error) {
logger.error('Get Lighting AI status error:', error);
      throw error;
    }
  },

  /**
   * Get Lighting AI Execution History
   * Uses QueryLightingAI with includeHistory parameter
   * @param aiGroupId - AI Group ID
   */
  async getLightingAIExecutionHistory(aiGroupId: string): Promise<any> {
    try {
logger.log('📜 Getting Lighting AI execution history for group:', aiGroupId);
      const result = await turingRequest('QueryLightingAI', {
        aiGroupId,
        includeHistory: true
      });
logger.log('✅ Lighting AI execution history:', result);
      return result;
    } catch (error) {
logger.error('Get Lighting AI execution history error:', error);
      throw error;
    }
  },

  /**
   * Calibrate Lighting AI Group
   * Auto-calibrates brightness-to-lux relationship through multi-step testing
   * Creates linear regression model: lux = a × brightness + b
   * @param aiGroupId - AI Group ID to calibrate
   */
  async calibrateLightingAI(aiGroupId: string): Promise<any> {
    try {
logger.log('🔬 Starting Lighting AI calibration for group:', aiGroupId);
      const result = await turingRequest('CalibrateLightingAI', {
        aiGroupId
      });
logger.log('✅ Lighting AI calibration started:', result);
      return result;
    } catch (error) {
logger.error('Calibrate Lighting AI error:', error);
      throw error;
    }
  },

  /**
   * Get Lighting AI Calibration Data
   * Retrieves calibration model and metadata for a group
   * @param aiGroupId - AI Group ID
   */
  async getLightingAICalibration(aiGroupId: string): Promise<any> {
    try {
logger.log('📊 Getting Lighting AI calibration data for group:', aiGroupId);
      const result = await turingRequest('QueryLightingAI', {
        aiGroupId,
        includeCalibration: true
      });
logger.log('✅ Lighting AI calibration data:', result);
      return result;
    } catch (error) {
logger.error('Get Lighting AI calibration error:', error);
      throw error;
    }
  },

  /**
   * Delete Lighting AI Calibration
   * Removes calibration data and reverts to standard adjustment modes
   * @param aiGroupId - AI Group ID
   */
  async deleteLightingAICalibration(aiGroupId: string): Promise<any> {
    try {
      // Check auth mode
      const authMode = sessionStorage.getItem('auth_mode');
      let headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Ultron-Cloud-Appid': 'KLMGeDMy'
      };

      if (authMode === 'oauth') {
        // ✅ OAuth mode - use Bearer token from sessionStorage
        const accessToken = getOAuthAccessToken();
        if (!accessToken) {
          throw new Error('Missing OAuth token. Please login again.');
        }
        headers['Authorization'] = `Bearer ${accessToken}`;
      } else {
        // API Key mode
        const credentials = getCredentials();
        const apiKey = String(credentials.apiKey || '').trim();
        const appId = String(credentials.appId || '').trim();
        headers['X-Api-Key'] = apiKey;
        headers['Ultron-Cloud-Appid'] = appId;
      }

      const url = `${getTuringApiUrl()}/api/v2/lighting-ai/groups/${aiGroupId}/calibration`;
logger.log('🗑️ Deleting Lighting AI calibration:', url);

      const response = await fetch(url, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });

      const data = await response.json();

      // 404 is OK - calibration was already deleted or doesn't exist
      if (response.status === 404) {
logger.log('⚠️ Calibration already deleted or not found:', data);
        return { success: true, message: 'Calibration already removed', wasAlreadyDeleted: true };
      }

      if (!response.ok || data.error || data.success === false) {
logger.error('❌ Failed to delete calibration:', data);
        const errorMsg = typeof data.error === 'object'
          ? JSON.stringify(data.error)
          : (data.error || data.message || 'Failed to delete calibration');
        throw new Error(errorMsg);
      }

logger.log('✅ Calibration deleted successfully:', data);
      return data;
    } catch (error) {
logger.error('Delete Lighting AI calibration error:', error);
      throw error;
    }
  },

  /**
   * Configure WebSocket subscription
   * 
   * @deprecated ConfigureWebSocket tool doesn't exist on Turing server.
   * Use subscribeDevices/unsubscribeDevices for Layer 1 webhook subscriptions instead.
   * WebSocket broadcasts all webhook updates to connected clients automatically.
   */
  async configureWebSocket(action: 'subscribe' | 'unsubscribe', devices: string[]): Promise<any> {
    logger.warn(`⚠️ configureWebSocket is deprecated - ConfigureWebSocket tool doesn't exist on server`);
    logger.log(`📡 Use subscribeDevices/unsubscribeDevices (Layer 1) instead`);
    
    // Redirect to Layer 1 API
    if (action === 'subscribe') {
      return this.subscribeDevices(devices);
    } else {
      return this.unsubscribeDevices(devices);
    }
  },

  /**
   * Sync webhook devices to UltronCloud
   * Ensures all account devices are subscribed to receive webhook pushes
   * Called when devices are imported (don't wait for hourly auto-sync)
   */
  async syncWebhookDevices(): Promise<any> {
    try {
      logger.log('📡 Syncing webhook devices to UltronCloud...');
      const result = await turingRequest('SyncWebhookDevices', {});
      logger.log('✅ Webhook devices synced:', result);
      return result;
    } catch (error) {
      logger.error('Sync webhook devices error:', error);
      throw error;
    }
  },

  /**
   * Subscribe devices to webhook (增量訂閱)
   * Layer 1: UltronCloud → Turing Server
   * Only subscribes specified devices to receive webhook pushes
   */
  async subscribeDevices(devices: string[]): Promise<any> {
    try {
      logger.log('📡 [Layer 1] Subscribing devices to webhook:', devices);
      const result = await turingRequest('SubscribeDevices', { devices });
      logger.log('✅ [Layer 1] Devices subscribed to webhook:', result);
      return result;
    } catch (error) {
      logger.error('[Layer 1] Subscribe devices error:', error);
      throw error;
    }
  },

  /**
   * Unsubscribe devices from webhook (增量取消訂閱)
   * Layer 1: UltronCloud → Turing Server
   * Removes specified devices from webhook push subscriptions
   */
  async unsubscribeDevices(devices: string[]): Promise<any> {
    try {
      logger.log('📡 [Layer 1] Unsubscribing devices from webhook:', devices);
      const result = await turingRequest('UnsubscribeDevices', { devices });
      logger.log('✅ [Layer 1] Devices unsubscribed from webhook:', result);
      return result;
    } catch (error) {
      logger.error('[Layer 1] Unsubscribe devices error:', error);
      throw error;
    }
  },

  /**
   * Query current webhook subscriptions
   * Layer 1: UltronCloud → Turing Server
   * Returns list of devices currently subscribed to webhook pushes
   */
  async querySubscriptions(): Promise<{ devices: string[], result?: number, deviceQuota?: { allocated: number, used: number } }> {
    try {
      logger.log('📡 [Layer 1] Querying webhook subscriptions...');
      const result = await turingRequest('QuerySubscribedDevices', {});
      logger.log('✅ [Layer 1] Webhook subscriptions raw:', result);
      
      // ✅ 正規化 devices: API 回傳 {} 或 { "SN1": {...}, "SN2": {...} }
      // 需要轉換成 string[] 格式
      let deviceList: string[] = [];
      if (result.devices) {
        if (Array.isArray(result.devices)) {
          deviceList = result.devices;
        } else if (typeof result.devices === 'object') {
          // devices 是物件時，取 keys 作為設備列表
          deviceList = Object.keys(result.devices);
        }
      }
      
      logger.log(`✅ [Layer 1] Normalized devices: ${deviceList.length} devices`);
      return { ...result, devices: deviceList };
    } catch (error) {
      logger.error('[Layer 1] Query subscriptions error:', error);
      throw error;
    }
  },

  /**
   * Sync all imported devices to webhook (增量同步 - Incremental Sync)
   *
   * Compares Firestore importedDevices (deduplicated) with Turing Server device list.
   * Calls add/remove APIs for differences only.
   *
   * Steps:
   * 1. Get all imported devices from Firestore (all boards) → Set A
   * 2. Get current Turing Server subscriptions via querySubscriptions() → Set B
   * 3. Calculate diff:
   *    - toAdd = A - B (devices in Firestore but not in Turing)
   *    - toRemove = B - A (devices in Turing but not in Firestore)
   * 4. Batch call subscribeDevices(toAdd) and unsubscribeDevices(toRemove)
   * 5. Return sync statistics
   */
  async syncAllImportedDevicesToWebhook(): Promise<any> {
    try {
      logger.log('🔄 [Webhook Sync] Starting incremental sync of imported devices...');

      // Step 1: Get all imported devices from Firestore (intelliSpace only - legacy boards removed)
      const { getImportedDevices } = await import('./userPreferencesApi');
      
      const firestoreDevices = new Set<string>();
      try {
        const boardDevices = await getImportedDevices('intelliSpace');
        
        // ✅ 使用 baseSN 格式（移除 #N 後綴），確保與 Turing Server 的格式一致
        boardDevices.forEach(device => {
          const deviceSN = (device as any).deviceSN || device.sn || (device as any).deviceId;
          if (deviceSN) {
            firestoreDevices.add(getBaseSN(deviceSN));
          }
        });
        logger.log(`📦 [Webhook Sync] intelliSpace: ${boardDevices.length} devices → ${firestoreDevices.size} unique baseSNs`);
      } catch (error) {
        logger.error('Failed to get imported devices for intelliSpace:', error);
      }

      logger.log(`📊 [Webhook Sync] Firestore total: ${firestoreDevices.size} unique devices`);
      if (firestoreDevices.size > 0) {
        logger.log(`📋 [Webhook Sync] Devices to sync:`, Array.from(firestoreDevices));
      }

      // ✅ 當 Firestore 為空時，清理所有訂閱和選擇（用戶刪除了所有設備或 Zone）
      if (firestoreDevices.size === 0) {
        logger.log('🧹 [Webhook Sync] No imported devices in Firestore - cleaning up all subscriptions and selections...');
        
        const cleanupResults = { removed: 0, websocketCleared: false, selectionsCleared: false, errors: [] as string[] };
        
        // Step 1: 清空 websocketEnabledDevices
        try {
          const { setWebsocketEnabledDevices } = await import('./userPreferencesApi');
          await setWebsocketEnabledDevices([]);
          cleanupResults.websocketCleared = true;
          logger.log('✅ [Cleanup] Cleared websocketEnabledDevices in Firestore');
        } catch (error) {
          logger.error('⚠️ [Cleanup] Failed to clear websocketEnabledDevices:', error);
          cleanupResults.errors.push(`Failed to clear websocketEnabledDevices: ${(error as Error).message}`);
        }
        
        // Step 2: 清空 intelliSpaceSelections (Dashboard 設備選擇)
        try {
          const { saveIntelliSpaceSelections } = await import('./userPreferencesApi');
          await saveIntelliSpaceSelections({
            powerMeters: [],
            switches: [],
            hvac: [],
            thSensors: [],
            lights: [],
            deviceOrder: [],
            excludedRankingDevices: []
          });
          cleanupResults.selectionsCleared = true;
          logger.log('✅ [Cleanup] Cleared intelliSpaceSelections in Firestore');
        } catch (error) {
          logger.error('⚠️ [Cleanup] Failed to clear intelliSpaceSelections:', error);
          cleanupResults.errors.push(`Failed to clear intelliSpaceSelections: ${(error as Error).message}`);
        }
        
        // Step 2.5: 清空 deviceOrder 和 selectedDevices（只保留 intelliSpace，移除 legacy fields）
        try {
          const { getUserPreferences, saveUserPreferences } = await import('./userPreferencesApi');
          const currentPrefs = await getUserPreferences();
          await saveUserPreferences({
            ...currentPrefs,
            // ✅ Only keep intelliSpace - remove legacy fields (energy, climate, hvac, lighting)
            deviceOrder: {
              intelliSpace: []
            },
            selectedDevices: {
              power24h: []
            }
          });
          logger.log('✅ [Cleanup] Cleared deviceOrder and selectedDevices in Firestore (legacy fields removed)');
        } catch (error) {
          logger.error('⚠️ [Cleanup] Failed to clear deviceOrder:', error);
          cleanupResults.errors.push(`Failed to clear deviceOrder: ${(error as Error).message}`);
        }
        
        // Step 3: 取消訂閱所有 Turing Server webhook devices
        try {
          const queryResult = await this.querySubscriptions();
          const turingDeviceList = queryResult.devices || [];
          if (turingDeviceList.length > 0) {
            logger.log(`📡 [Cleanup] Found ${turingDeviceList.length} webhook subscriptions, unsubscribing all...`);
            await this.unsubscribeDevices(turingDeviceList);
            cleanupResults.removed = turingDeviceList.length;
            logger.log(`✅ [Cleanup] Unsubscribed ${turingDeviceList.length} devices from webhook`);
          } else {
            logger.log('📡 [Cleanup] No webhook subscriptions to clear');
          }
        } catch (error) {
          logger.error('⚠️ [Cleanup] Failed to unsubscribe webhook devices:', error);
          cleanupResults.errors.push(`Failed to unsubscribe webhook devices: ${(error as Error).message}`);
        }
        
        // Step 4: 清空 localStorage 快取 (包括舊版資料)
        try {
          const localStorageKeys = [
            // IntelliSpace imported devices
            'imported-devices-intelliSpace',
            'intellispace-devices-cache',
            'websocket-enabled-devices',
            // IntelliSpace selections
            'intellispace-selected-power-meters',
            'intellispace-selected-switches',
            'intellispace-selected-hvac',
            'intellispace-selected-th-sensors',
            'intellispace-selected-lights',
            'intellispace-selected-hvac-meters',
            'intellispace-selected-lighting-meters',
            'intellispace-selected-three-phase-meter',
            'intellispace-excluded-ranking-devices',
            // Legacy imported devices (old boards)
            'imported-devices-energy',
            'imported-devices-climate',
            'imported-devices-hvac',
            'imported-devices-lighting',
            // Legacy device order
            'power-meter-order',
            'climateDeviceOrder',
            'hvacDeviceOrder',
            // Legacy WebSocket subscriptions
            'ws-subscriptions-lighting',
            'ws-subscriptions-climate'
          ];
          localStorageKeys.forEach(key => {
            if (typeof localStorage !== 'undefined') {
              localStorage.removeItem(key);
            }
          });
          logger.log('✅ [Cleanup] Cleared localStorage cache (including legacy data)');
        } catch (error) {
          logger.warn('⚠️ [Cleanup] Failed to clear localStorage:', error);
        }
        
        return { 
          success: cleanupResults.errors.length === 0, 
          cleanup: true, 
          removed: cleanupResults.removed,
          websocketCleared: cleanupResults.websocketCleared,
          selectionsCleared: cleanupResults.selectionsCleared,
          errors: cleanupResults.errors.length > 0 ? cleanupResults.errors : undefined
        };
      }

      // Step 2: Get current Turing Server subscriptions via MCP API
      const turingDevices = new Set<string>();
      try {
        const queryResult = await this.querySubscriptions();
        const turingDeviceList = queryResult.devices || [];
        // ✅ FIX: Normalize Turing SNs with getBaseSN() to match Firestore format
        // This ensures consistent comparison between sets (both use baseSN without #N suffix)
        turingDeviceList.forEach((sn: string) => turingDevices.add(getBaseSN(sn)));
        logger.log(`📡 [Webhook Sync] Turing Server: ${turingDevices.size} subscribed devices (normalized)`);
      } catch (error) {
        logger.error('❌ [Webhook Sync] Failed to get Turing devices, will perform full sync:', error);
        // Fallback to full sync if can't get Turing devices
        const firestoreArray = Array.from(firestoreDevices);
        if (firestoreArray.length > 0) {
          const result = await this.subscribeDevices(firestoreArray);
          logger.log('✅ [Webhook Sync] Full sync fallback completed');
          return { success: true, added: firestoreArray.length, removed: 0, fallback: true };
        }
        return { success: true, message: 'No devices to sync' };
      }

      // Step 3: Calculate differences
      const toAdd = Array.from(firestoreDevices).filter(sn => !turingDevices.has(sn));
      const toRemove = Array.from(turingDevices).filter(sn => !firestoreDevices.has(sn));

      logger.log(`➕ [Webhook Sync] To add: ${toAdd.length} devices`, toAdd.length > 0 ? toAdd : '(none)');
      logger.log(`➖ [Webhook Sync] To remove: ${toRemove.length} devices`, toRemove.length > 0 ? toRemove : '(none)');

      // Step 4: Perform incremental sync
      const results = { added: 0, removed: 0, errors: [] as string[] };

      // Add new devices
      if (toAdd.length > 0) {
        try {
          await this.subscribeDevices(toAdd);
          results.added = toAdd.length;
          logger.log(`✅ [Webhook Sync] Successfully added ${toAdd.length} devices`);
        } catch (error: any) {
          logger.error('❌ [Webhook Sync] Failed to add devices:', error);
          results.errors.push(`Failed to add ${toAdd.length} devices: ${error.message}`);
        }
      }

      // ✅ 安全檢查 2：如果要刪除所有設備，拒絕執行（防止 SN 格式不匹配導致誤刪）
      if (toRemove.length > 0 && turingDevices.size > 0 && toRemove.length === turingDevices.size) {
        logger.error('❌ [Webhook Sync] Safety check failed: Would delete ALL devices!');
        logger.error(`  - Firestore devices (baseSN): ${Array.from(firestoreDevices).join(', ')}`);
        logger.error(`  - Turing devices: ${Array.from(turingDevices).join(', ')}`);
        logger.error(`  - This usually indicates SN format mismatch - aborting sync`);
        return {
          success: false,
          error: 'safety_check_failed',
          message: 'Would delete all devices - possible SN format mismatch',
          wouldDelete: toRemove.length,
          firestoreDevices: Array.from(firestoreDevices),
          turingDevices: Array.from(turingDevices)
        };
      }

      // Remove old devices
      if (toRemove.length > 0) {
        try {
          await this.unsubscribeDevices(toRemove);
          results.removed = toRemove.length;
          logger.log(`✅ [Webhook Sync] Successfully removed ${toRemove.length} devices`);
        } catch (error: any) {
          logger.error('❌ [Webhook Sync] Failed to remove devices:', error);
          results.errors.push(`Failed to remove ${toRemove.length} devices: ${error.message}`);
        }
      }

      // Step 5: Log summary
      if (results.added === 0 && results.removed === 0) {
        logger.log('✅ [Webhook Sync] Already in sync, no changes needed');
      } else {
        logger.log(`✅ [Webhook Sync] Incremental sync completed: +${results.added}, -${results.removed}`);
      }

      // Step 6: 同步到 websocketEnabledDevices (Layer 2 - App ↔ Turing Server)
      // 這確保所有 importedDevices 自動啟用 WebSocket 即時更新
      try {
        const { setWebsocketEnabledDevices } = await import('./userPreferencesApi');
        const allBaseSNs = Array.from(firestoreDevices);
        await setWebsocketEnabledDevices(allBaseSNs);
        logger.log(`✅ [WebSocket Sync] Auto-enabled ${allBaseSNs.length} devices for WebSocket`);
      } catch (wsError) {
        logger.error('⚠️ [WebSocket Sync] Failed to sync websocketEnabledDevices:', wsError);
        // 不要因為 WebSocket 同步失敗而中斷整個流程
        results.errors.push(`WebSocket sync failed: ${(wsError as Error).message}`);
      }

      return {
        success: results.errors.length === 0,
        added: results.added,
        removed: results.removed,
        errors: results.errors.length > 0 ? results.errors : undefined
      };
    } catch (error) {
      logger.error('❌ [Webhook Sync] Failed to sync imported devices to webhook:', error);
      throw error;
    }
  },

  /**
   * Query all scenes for a location
   */
  async queryScenes(locationId?: string): Promise<ApiResponse<unknown>> {
    try {
logger.log('🎬 Querying scenes for location:', locationId || 'all locations');
      const params: Record<string, unknown> = {};
      if (locationId) {
        params.locationId = locationId;
      }
      const result = await turingRequest('QueryScenes', params);
logger.log('✅ Scenes retrieved:', result);
      return result;
    } catch (error) {
logger.error('Query scenes error:', error);
      throw error;
    }
  },

  /**
   * Configure scene (create, update, or delete)
   */
  async configureScene(config: {
    action: 'create' | 'update' | 'delete';
    id?: string;
    name?: string;
    locationId?: string;
    devices?: Array<{ sn: string; state: any }>;
  }): Promise<any> {
    try {
logger.log('🎬 Configuring scene:', config);
      const result = await turingRequest('ConfigureScene', config);
logger.log('✅ Scene configured:', result);
      return result;
    } catch (error) {
logger.error('Configure scene error:', error);
      throw error;
    }
  },

  /**
   * Run/execute a saved scene
   */
  async runScene(sceneId: string, locationId: string): Promise<any> {
    try {
logger.log('🎬 Running scene:', sceneId);
      const result = await turingRequest('RunScene', {
        sceneId,
        locationId
      });
logger.log('✅ Scene executed:', result);
      return result;
    } catch (error) {
logger.error('Run scene error:', error);
      throw error;
    }
  },

  /**
   * Query all automations for a location
   */
  async queryAutomations(locationId?: string): Promise<ApiResponse<unknown>> {
    try {
logger.log('🤖 Querying automations for location:', locationId || 'all locations');
      const params: Record<string, unknown> = {};
      if (locationId) {
        params.locationId = locationId;
      }
      const result = await turingRequest('QueryAutomations', params);
logger.log('✅ Automations retrieved:', result);
      return result;
    } catch (error) {
logger.error('Query automations error:', error);
      throw error;
    }
  },

  /**
   * Query all configured alerts
   * Note: QueryAlerts API was removed, now using ConfigureAlert with action: 'query'
   */
  async queryAlerts(locationId?: string, deviceSN?: string): Promise<ApiResponse<unknown>> {
    try {
      logger.log('🔔 Querying alerts via ConfigureAlert');
      const params: Record<string, unknown> = {
        action: 'query',
        name: '' // Required field, empty for query
      };
      if (locationId) {
        params.locationId = locationId;
      }
      if (deviceSN) {
        params.deviceSN = deviceSN;
      }
      const result = await turingRequest('ConfigureAlert', params);
      logger.log('✅ Alerts retrieved:', result);
      return result;
    } catch (error) {
      logger.error('Query alerts error:', error);
      throw error;
    }
  },

  /**
   * Manage API Keys (create, list, revoke, store)
   */
  async manageApiKeys(params: {
    action: 'create' | 'list' | 'revoke' | 'store';
    name?: string;
    appId?: string;
    apiKey?: string;
    purpose?: string;
    filterByAppId?: string;
  }): Promise<any> {
    try {
logger.log('🔑 Managing API Keys:', params.action);
      const result = await turingRequest('ManageApiKeys', params);
logger.log('✅ API Key operation complete:', result);
      return result;
    } catch (error) {
logger.error('Manage API Keys error:', error);
      throw error;
    }
  },

  /**
   * Configure device name/nickname
   * - deviceName: UltronCloud device name (global, shared across all apps)
   * - nickname: Turing local nickname (per-tenant, stored in Shadow only)
   * Display priority: nickname > deviceName > sn
   */
  async configureDevice(deviceSN: string, options: { 
    deviceName?: string;  // Global device name (syncs to UltronCloud)
    nickname?: string | null;  // Tenant-local nickname (set to null to clear)
  }): Promise<any> {
    try {
      logger.log('📝 Configuring device:', deviceSN, options);
      const result = await turingRequest('ConfigureDevice', {
        deviceSN,
        ...options
      });
      logger.log('✅ Device configured:', result);
      return result;
    } catch (error) {
      logger.error('Configure device error:', error);
      throw error;
    }
  },

  /**
   * @deprecated Use configureDevice instead
   * Configure device nickname (multi-tenant device aliasing)
   */
  async configureDeviceNickname(deviceSN: string, nickname: string | null): Promise<any> {
    return this.configureDevice(deviceSN, { nickname });
  },

  /**
   * Refresh device cache (force re-sync from UltronCloud)
   */
  async refreshDeviceCache(force: boolean = true): Promise<any> {
    try {
logger.log('🔄 Refreshing device cache, force:', force);
      const result = await turingRequest('RefreshDeviceCache', {
        force
      });
logger.log('✅ Device cache refresh initiated:', result);
      return result;
    } catch (error) {
logger.error('Refresh device cache error:', error);
      throw error;
    }
  },

  /**
   * Configure tenant settings (timezone, language, etc.)
   */
  async configureTenant(params: {
    timezone?: string;
    language?: string;
  }): Promise<any> {
    try {
logger.log('⚙️ Configuring tenant settings:', params);
      const result = await turingRequest('ConfigureTenant', params);
logger.log('✅ Tenant settings configured:', result);
      return result;
    } catch (error) {
logger.error('Configure tenant error:', error);
      throw error;
    }
  },

  /**
   * Get sensor data summary (aggregated statistics)
   * More efficient than GenerateReport for hourly/daily aggregates
   *
   * @param sn - Device serial number (baseSN format) - single device query
   * @param sns - Device serial numbers array - batch query (preferred)
   * @param startTime - ISO 8601 start time
   * @param endTime - ISO 8601 end time
   * @param types - Sensor types: watt/voltage/current/watt_hour for power meters
   * @param category - Granularity: 'date' (daily) or 'hour' (hourly)
   * @param iotDevs - IoT endpoints (typically ["1"] for power meters)
   */
  async getSensorDataSummary(params: {
    sn?: string;          // Single device (backward compatible)
    sns?: string[];       // Batch query (preferred - reduces API calls)
    startTime: string;
    endTime: string;
    types?: string[];
    category?: 'date' | 'hour';
    iotDevs?: string[];
    page?: number;
    pageSize?: number;
  }): Promise<any> {
    try {
      const requestParams: Record<string, unknown> = {
        startTime: params.startTime,
        endTime: params.endTime,
        types: params.types || ['watt'],
        category: params.category || 'hour',
        iotDevs: params.iotDevs || ['1'],
        page: params.page || 1,
        pageSize: params.pageSize
      };

      // Batch query takes priority (reduces API quota usage)
      if (params.sns && params.sns.length > 0) {
        requestParams.sns = params.sns;
        logger.log('📊 GetSensorDataSummary batch request:', params.sns.length, 'devices');
      } else if (params.sn) {
        requestParams.sn = params.sn;
        logger.log('📊 GetSensorDataSummary single request:', params.sn);
      }

      const result = await turingRequest('GetSensorDataSummary', requestParams);
      logger.log('📊 GetSensorDataSummary result received');
      return result;
    } catch (error) {
      logger.error('GetSensorDataSummary error:', error);
      throw error;
    }
  },

  /**
   * Query Alert History - Get historical alerts with filtering and pagination
   * @param params - Query parameters for alert history
   */
  async queryAlertHistory(params: {
    locationId?: string;
    deviceSN?: string;
    severity?: ('info' | 'warning' | 'major' | 'critical')[];
    alertType?: ('climate' | 'power' | 'battery')[];
    startTime?: string;
    endTime?: string;
    limit?: number;
    offset?: number;
    includeRead?: boolean;
  }): Promise<{
    success: boolean;
    data?: {
      alerts: Array<{
        alertId: string;
        alertName: string;
        deviceSn: string;
        deviceName: string;
        metric: string;
        condition: string;
        threshold: number;
        currentValue: number;
        severity: string;
        type: 'triggered' | 'recovered';
        timestamp: string;
        read?: boolean;
        acknowledgedAt?: string | null;
        resolvedAt?: string | null;
      }>;
      total: number;
      unreadCount: number;
      hasMore: boolean;
    };
  }> {
    try {
      const result = await turingRequest('QueryAlertHistory', params);
      logger.log('📋 Alert history queried:', result);
      return result;
    } catch (error) {
      logger.error('QueryAlertHistory error:', error);
      throw error;
    }
  },

  /**
   * Mark Alert as Read - Mark one or more alerts as read/acknowledged
   * @param params - Alert IDs and action
   */
  async markAlertRead(params: {
    alertIds?: string[];
    action: 'read' | 'unread' | 'readAll';
    locationId?: string;
    beforeTime?: string;
  }): Promise<{
    success: boolean;
    updated: number;
    alerts?: Array<{
      alertId: string;
      read: boolean;
      acknowledgedAt: string;
    }>;
  }> {
    try {
      const result = await turingRequest('MarkAlertRead', params);
      logger.log('✅ Alerts marked as read:', result);
      return result;
    } catch (error) {
      logger.error('MarkAlertRead error:', error);
      throw error;
    }
  },

  /**
   * Query Alert Statistics - Get aggregated alert statistics
   * @param params - Statistics query parameters
   */
  async queryAlertStatistics(params: {
    locationId?: string;
    startTime: string;
    endTime: string;
    groupBy?: 'hour' | 'day' | 'week';
  }): Promise<{
    success: boolean;
    data?: {
      summary: {
        total: number;
        critical: number;
        warning: number;
        info: number;
      };
      bySeverity: {
        critical: number;
        major: number;
        warning: number;
        info: number;
      };
      byType: {
        climate: number;
        power: number;
        battery: number;
      };
      byDevice: Record<string, number>;
      timeline: Array<{
        date: string;
        count: number;
        critical: number;
        warning: number;
      }>;
    };
  }> {
    try {
      const result = await turingRequest('QueryAlertStatistics', params);
      logger.log('📊 Alert statistics queried:', result);
      return result;
    } catch (error) {
      logger.error('QueryAlertStatistics error:', error);
      throw error;
    }
  },

  /**
   * Query Notifications - Get notifications from Firestore persistence layer
   * Supports list, count, mark read, and delete operations
   * @param options - Query options
   */
  async queryNotifications(options?: {
    action?: 'list' | 'list_all' | 'count' | 'mark_read' | 'mark_all_read' | 'delete';
    notificationId?: string;
    limit?: number;
  }): Promise<{
    success: boolean;
    data?: {
      notifications?: Array<{
        id: string;
        type: string;
        title: string;
        message: string;
        deviceSn?: string;
        timestamp: string;
        read: boolean;
        severity?: string;
        metric?: string;
      }>;
      count?: number;
    };
    error?: string;
  }> {
    try {
      const params: Record<string, unknown> = {
        action: options?.action || 'list'
      };
      
      if (options?.notificationId) {
        params.notificationId = options.notificationId;
      }
      if (options?.limit) {
        params.limit = options.limit;
      }
      
      const result = await turingRequest('QueryNotifications', params);
      logger.log('🔔 Notifications queried:', result);
      return result;
    } catch (error) {
      logger.error('QueryNotifications error:', error);
      throw error;
    }
  },

  /**
   * Get Notification Preferences - Get user's notification channel preferences
   * Returns preferences for alert, automation, and battery notification types
   */
  async getNotificationPreferences(): Promise<{
    success: boolean;
    data?: {
      preferences?: NotificationPreferences;
    };
    error?: string;
  }> {
    try {
      const params = {
        action: 'get_preferences'
      };
      
      const result = await turingRequest('QueryNotifications', params);
      logger.log('🔔 Notification preferences retrieved:', result);
      return {
        success: true,
        data: {
          preferences: result?.preferences || result?.data?.preferences || {
            alert: ['websocket', 'email'],
            automation: ['websocket', 'email'],
            battery: ['websocket', 'email']
          }
        }
      };
    } catch (error) {
      logger.error('GetNotificationPreferences error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get notification preferences'
      };
    }
  },

  /**
   * Set Notification Preferences - Update user's notification channel preferences
   * @param preferences - Notification preferences for alert, automation, and battery types
   */
  async setNotificationPreferences(preferences: NotificationPreferences): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const params = {
        action: 'set_preferences',
        preferences
      };
      
      await turingRequest('QueryNotifications', params);
      logger.log('🔔 Notification preferences saved:', preferences);
      return { success: true };
    } catch (error) {
      logger.error('SetNotificationPreferences error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to save notification preferences'
      };
    }
  },

  /**
   * Test Notification Email - Send a test email to verify notification settings
   * Uses QueryNotifications with action: 'test_email'
   * 
   * Note: Before calling test_email, ensure notification preferences are saved with
   * the email address. The test_email action uses the saved notificationEmail from preferences.
   * 
   * @param email - Optional email address - will save to preferences first before testing
   */
  async testNotificationEmail(email?: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      logger.log('📧 testNotificationEmail called with email:', email);
      
      // If email is provided, first save it to preferences
      if (email) {
        logger.log('📧 Setting notificationEmail before test:', email);
        const saveResult = await this.setNotificationPreferences({
          notificationEmail: email
        });
        logger.log('📧 Save preferences result:', saveResult);
        if (!saveResult.success) {
          logger.error('Failed to save email preference before test:', saveResult.error);
          return {
            success: false,
            error: saveResult.error || 'Failed to save email preference'
          };
        }
        
        // Wait a moment for the preference to be persisted
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Now send test email
      logger.log('📧 Sending test email...');
      const params = {
        action: 'test_email'
      };
      
      const result = await turingRequest('QueryNotifications', params);
      logger.log('📧 Test email sent:', result);
      return { 
        success: true,
        message: result?.message || 'Test email sent successfully'
      };
    } catch (error) {
      logger.error('TestNotificationEmail error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send test email'
      };
    }
  },

  /**
   * Analyze device operation logs
   * Returns detailed analysis report including operation sources, time patterns, behavior patterns
   * @param sn - Device serial number (required)
   * @param options - Optional parameters
   * @param options.period - Time range: 'week' (default) or 'twoWeeks'
   * @param options.format - Report format: 'summary' or 'detailed' (default)
   */
  async analyzeDeviceLogs(
    sn: string,
    options?: {
      period?: 'week' | 'twoWeeks';
      format?: 'summary' | 'detailed';
    }
  ): Promise<{
    success: boolean;
    data?: {
      deviceSn: string;
      period: string;
      report: string;
      summary?: {
        totalOperations: number;
        operationsBySource: Record<string, number>;
        operationsByCommand: Record<string, number>;
        timePatterns?: Record<string, number>;
      };
    };
    error?: string;
  }> {
    try {
      if (!sn) {
        return {
          success: false,
          error: 'Device SN is required'
        };
      }

      const params: Record<string, unknown> = {
        sn,
        period: options?.period || 'week',
        format: options?.format || 'detailed'
      };

      const result = await turingRequest('AnalyzeDeviceLogs', params);
      logger.log('📊 Device logs analyzed:', result);
      return {
        success: true,
        data: result
      };
    } catch (error) {
      logger.error('AnalyzeDeviceLogs error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to analyze device logs'
      };
    }
  }
};